import React, {useEffect, useState, useRef} from "react";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {supabase} from "./utils/supabaseClient";
import { applyTenantDocumentTitle, getTenantBranding, isBankTransferConfigured } from "./utils/tenantBranding";
import {
    Alert,
    Box,
    Button,
    Center,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Select,
    Spinner,
    Stack,
    Text,
    AlertIcon,
    useToast,
    Badge,
    SimpleGrid,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Flex,
    Icon,
    IconButton,
    Checkbox,
    Divider,
    Radio,
    RadioGroup,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Textarea,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Card,
    CardBody,
    CardHeader,
    HStack,
    CopyIcon,
    LightMode
} from "@chakra-ui/react";
import {HiArrowLeft, HiDocumentText, HiCamera, HiAcademicCap, HiCreditCard, HiDeviceMobile, HiChevronDown, HiChevronUp, HiQrcode, HiOfficeBuilding} from 'react-icons/hi';
import { FaHandPointer } from 'react-icons/fa';
import PhoneInput, { isValidPhoneNumber as isValidPhone, parsePhoneNumber as parsePhone } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {parsePhoneNumber, isValidPhoneNumber} from 'libphonenumber-js';
import moment from 'moment';
import NRIC from 'singapore-nric';
import AdditionalFields from "./AdditionalFields";
import SignaturePad from 'react-signature-canvas';
import { motion } from "framer-motion";
import { useRouteConfig } from "./providers/RouteConfigProvider";
import HelpPopup from './components/HelpPopup';
import AgeEligibilityAlert, { isUnderMinimumRegistrationAge } from './components/AgeEligibilityAlert';
import { PayNowQRCode, useOrganizationPayNowDetails } from './components/PayNowQRCode';
import { FileAttachmentRow } from './components/FileAttachmentRow';
import { buildPayNowReference } from './utils/paynowReference';
import {
    isSingaporeCitizenOrPR,
    isNonSingaporeResidentPassport,
    shouldApplyGst,
    shouldAskTakingCourseInSingapore,
} from './utils/locationBasedGst';
import { fetchIsStripePaymentConfigured } from './utils/stripePaymentAvailability';
import { createPendingRegistrationFeeInvoice } from './utils/createPendingRegistrationFeeInvoice';
import { Select as SearchableSelect } from "chakra-react-select";

const isDevelopment = process.env.NODE_ENV === 'development';

const devLog = (...args) => {
    if (isDevelopment) {
        console.log(...args);
    }
};

// Fallback option values for nationality/ID-type when field_options is empty (values stored as-is; display uses field_behaviors.option_labels when present)
const DEFAULT_NATIONALITY_OPTION_VALUES = [
    "Singapore Citizen / PR",
    "Work Pass/ E-Pass / LTVP (FIN)",
    "Non-Singapore Resident (Passport No)"
];

// Floating Progress Bar Component - shows compulsory field completion
const FloatingProgressBar = ({ 
    filledCount, 
    totalCount, 
    emailVerified, 
    emailVerificationSent,
    participantEmail,
    onResendVerification,
    isSendingVerification,
    showEmailVerification
}) => {
    const progressPercent = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;
    const isComplete = filledCount === totalCount;
    
    return (
        <Box
            position="sticky"
            top={0}
            left={0}
            right={0}
            zIndex={100}
            bg="white"
            borderBottom="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            transition="all 0.3s ease"
        >
            {/* Progress Bar */}
            <Box px={{base: 4, md: 6}} py={{base: 2, md: 3}}>
                <Flex align="center" justify="space-between" mb={1}>
                    <HStack spacing={2}>
                        <Text fontSize={{base: "xs", md: "sm"}} fontWeight="600" color="gray.700">
                            {isComplete ? "✓ All fields complete" : "Form Progress"}
                        </Text>
                    </HStack>
                    <Badge 
                        colorScheme={isComplete ? "green" : progressPercent > 50 ? "blue" : "orange"}
                        fontSize={{base: "10px", md: "xs"}}
                        px={2}
                    >
                        {filledCount}/{totalCount}
                    </Badge>
                </Flex>
                <Box 
                    w="100%" 
                    h={{base: "4px", md: "6px"}} 
                    bg="gray.100" 
                    borderRadius="full" 
                    overflow="hidden"
                >
                    <Box
                        h="100%"
                        w={`${progressPercent}%`}
                        bgGradient={isComplete 
                            ? "linear(to-r, green.400, green.500)" 
                            : "linear(to-r, brand.400, brand.600)"
                        }
                        borderRadius="full"
                        transition="width 0.4s ease-out"
                    />
                </Box>
            </Box>
            
            {/* Email Verification Notice - Only shows when needed */}
            {showEmailVerification && participantEmail && !emailVerified && (
                <Box 
                    px={{base: 4, md: 6}} 
                    py={{base: 2, md: 2.5}}
                    bg="orange.50"
                    borderTop="1px solid"
                    borderColor="orange.100"
                >
                    <Flex 
                        align="center" 
                        justify="space-between"
                        gap={2}
                        flexWrap={{base: "wrap", md: "nowrap"}}
                    >
                        <HStack spacing={2} flex="1" minW={0}>
                            <Text fontSize="md">📧</Text>
                            <Text 
                                fontSize={{base: "xs", md: "sm"}} 
                                color="orange.800"
                                fontWeight="500"
                                isTruncated
                            >
                                Verify: <Text as="span" fontWeight="600">{participantEmail}</Text>
                            </Text>
                        </HStack>
                        <Button
                            size="xs"
                            colorScheme="orange"
                            variant="solid"
                            onClick={onResendVerification}
                            isLoading={isSendingVerification}
                            loadingText="..."
                            flexShrink={0}
                        >
                            Resend
                        </Button>
                    </Flex>
                </Box>
            )}
            
            {/* Email Verified Success - Brief confirmation */}
            {showEmailVerification && emailVerified && (
                <Box 
                    px={{base: 4, md: 6}} 
                    py={2}
                    bg="green.50"
                    borderTop="1px solid"
                    borderColor="green.100"
                >
                    <HStack spacing={2}>
                        <Text fontSize="sm">✓</Text>
                        <Text fontSize="xs" color="green.700" fontWeight="600">
                            Email verified
                        </Text>
                    </HStack>
                </Box>
            )}
        </Box>
    );
};

const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const validateNRIC = (nric, nationality) => {
    if (!nric) return false;
    
    // Only validate NRIC format for Singapore Citizens and PRs
    // For other nationalities, allow free text (passport/FIN numbers)
    // Support both old and new option values
    const isSingaporeCitizenOrPR = 
        nationality === "Singapore Citizen" || 
        nationality === "Singapore Permanent Resident" ||
        nationality === "Singapore Citizen / PR";
    
    if (!isSingaporeCitizenOrPR) {
        // For non-Singapore citizens/PRs, just check that something is entered
        return nric.trim().length > 0;
    }
    
    // For Singapore citizens/PRs, validate NRIC format
    const nricInstance = new NRIC(nric);
    return nricInstance.isValid && nricInstance.isCorrectFormat;
};

// Enhanced calendar component for module dates with duration
const ModuleCalendar = ({course_dates, moduleCode}) => {
    if (!course_dates || course_dates.length === 0) return null;

    const sessionsWithDuration = course_dates.map(session => {
        const startTime = moment(session.start_time, "HHmm");
        const endTime = moment(session.end_time, "HHmm");
        const duration = endTime.diff(startTime, 'hours', true);

        return {
            ...session,
            duration: duration,
            durationText: duration >= 1 ? `${Math.floor(duration)}h ${Math.round((duration % 1) * 60)}m` : `${Math.round(duration * 60)}m`
        };
    });

    const datesByMonth = sessionsWithDuration.reduce((acc, session) => {
        const date = new Date(session.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        const monthName = date.toLocaleDateString('en-US', {month: 'short', year: 'numeric'});

        if (!acc[monthKey]) {
            acc[monthKey] = {
                monthName,
                dates: []
            };
        }

        acc[monthKey].dates.push({
            day: date.getDate(),
            startTime: session.start_time,
            endTime: session.end_time,
            duration: session.duration,
            durationText: session.durationText,
            dayOfWeek: date.toLocaleDateString('en-US', {weekday: 'short'})
        });

        return acc;
    }, {});

    const totalDuration = sessionsWithDuration.reduce((sum, session) => sum + session.duration, 0);
    const totalHours = Math.floor(totalDuration);
    const totalMinutes = Math.round((totalDuration % 1) * 60);

    return (
        <Box mt={2}>
            <Text fontSize="xs" fontWeight="700" color="gray.600" mb={2.5} textTransform="uppercase" letterSpacing="0.5px">
                Schedule
            </Text>
            <VStack spacing={3} align="stretch">
                {Object.values(datesByMonth).map((month, index) => (
                    <Box key={index}>
                        <Text fontSize="xs" fontWeight="700" color="gray.700" mb={2} textTransform="uppercase" letterSpacing="0.5px">
                            {month.monthName}
                        </Text>

                        <Box overflowX="auto" borderRadius="md" border="1px solid" borderColor="gray.200">
                            <Table variant="simple" size="sm" colorScheme="gray">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th fontSize="xs" fontWeight="700" color="gray.700" p={2.5} borderBottom="1px solid" borderColor="gray.200">Date</Th>
                                        <Th fontSize="xs" fontWeight="700" color="gray.700" p={2.5} borderBottom="1px solid" borderColor="gray.200">Time</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {month.dates.map((date, dateIndex) => {
                                        const startTimeFormatted = moment(date.startTime, "HHmm").format("h:mm A");
                                        const endTimeFormatted = moment(date.endTime, "HHmm").format("h:mm A");
                                        const timeRange = `${startTimeFormatted} - ${endTimeFormatted}`;
                                        
                                        return (
                                            <Tr key={dateIndex} _hover={{ bg: "blue.50" }} transition="background 0.2s">
                                                <Td fontSize="xs" p={2.5} borderBottom="1px solid" borderColor="gray.100">
                                                    <VStack spacing={0.5} align="start">
                                                        <Text fontWeight="700" fontSize="sm" color="gray.800">{date.day}</Text>
                                                        <Text fontSize="10px" color="gray.600" textTransform="capitalize" fontWeight="500">
                                                            {date.dayOfWeek}
                                                        </Text>
                                                    </VStack>
                                                </Td>
                                                <Td fontSize="xs" p={2.5} borderBottom="1px solid" borderColor="gray.100">
                                                    <Text fontWeight="600" fontSize="xs" color="gray.800">
                                                        {timeRange}
                                                    </Text>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                ))}
            </VStack>
        </Box>
    );
};

// Module card component for displaying certification modules with flip animation
function ModuleCard({course_name, start_date, end_date, course_dates, course_type_code, module_tag}) {
    const [isFlipped, setIsFlipped] = useState(false);
    const frontCardRef = useRef(null);
    const backCardRef = useRef(null);
    const containerRef = useRef(null);
    
    // Format dates for display
    const startDateFormatted = start_date ? moment(start_date).format('MMM DD, YYYY') : 'TBD';
    const endDateFormatted = end_date ? moment(end_date).format('MMM DD, YYYY') : 'TBD';
    const isUpcoming = start_date ? moment(start_date).isAfter(moment()) : false;
    const daysUntilStart = start_date ? moment(start_date).diff(moment(), 'days') : null;
    
    // Get first session date for sorting/display
    const firstSessionDate = course_dates && course_dates.length > 0 
        ? course_dates[0]?.date 
        : start_date;

    // Prepare schedule data for the back of the card
    const sessionsWithDuration = (course_dates || []).map(session => {
        const startTime = moment(session.start_time, "HHmm");
        const endTime = moment(session.end_time, "HHmm");
        const duration = endTime.diff(startTime, 'hours', true);
        return {
            ...session,
            duration: duration,
            durationText: duration >= 1 ? `${Math.floor(duration)}h ${Math.round((duration % 1) * 60)}m` : `${Math.round(duration * 60)}m`
        };
    });

    const datesByMonth = sessionsWithDuration.reduce((acc, session) => {
        const date = new Date(session.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        const monthName = date.toLocaleDateString('en-US', {month: 'short', year: 'numeric'});

        if (!acc[monthKey]) {
            acc[monthKey] = {
                monthName,
                dates: []
            };
        }

        acc[monthKey].dates.push({
            day: date.getDate(),
            startTime: session.start_time,
            endTime: session.end_time,
            duration: session.duration,
            durationText: session.durationText,
            dayOfWeek: date.toLocaleDateString('en-US', {weekday: 'short'})
        });

        return acc;
    }, {});

    // Update container height based on visible card
    useEffect(() => {
        const updateHeight = () => {
            if (containerRef.current) {
                const visibleCard = isFlipped ? backCardRef.current : frontCardRef.current;
                if (visibleCard) {
                    containerRef.current.style.height = `${visibleCard.offsetHeight}px`;
                }
            }
        };
        
        updateHeight();
        
        // Also update on window resize
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [isFlipped]);

    return (
        <Box
            ref={containerRef}
            cursor="pointer"
            onClick={() => setIsFlipped(!isFlipped)}
            sx={{
                perspective: '1000px'
            }}
            transition="height 0.6s"
        >
            <Box
                position="relative"
                width="100%"
                transition="transform 0.6s"
                sx={{
                    transformStyle: 'preserve-3d',
                    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}
            >
                {/* Front of card */}
                <Box
                    ref={frontCardRef}
                    position="absolute"
                    width="100%"
                    borderWidth='1px'
                    borderRadius='xl'
                    boxShadow='sm'
                    borderColor="gray.200"
                    bg="white"
                    px={{base: 3, md: 4}}
                    py={{base: 3, md: 4}}
                    wordBreak="break-word"
                    display="flex"
                    flexDirection="column"
                    transition="box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out"
                    _hover={{
                        boxShadow: 'lg',
                        borderColor: 'brand.400'
                    }}
                    overflow="hidden"
                    sx={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden'
                    }}
                >
                    {/* Accent bar at top */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height="4px"
                        bgGradient="linear(to-r, brand.400, brand.600)"
                    />
                    
                    <VStack align="stretch" spacing={3} flex={1}>
                        {/* Header with badge and code */}
                        <HStack justify="space-between" align="flex-start">
                            <Badge 
                                colorScheme="blue" 
                                fontSize="xs" 
                                px={2.5} 
                                py={1} 
                                borderRadius="md"
                                fontWeight="600"
                                textTransform="uppercase"
                                letterSpacing="0.5px"
                            >
                                {course_type_code}
                            </Badge>
                            {isUpcoming && daysUntilStart !== null && daysUntilStart >= 0 && (
                                <Badge 
                                    colorScheme={daysUntilStart <= 7 ? "orange" : "green"} 
                                    fontSize="xs" 
                                    px={2} 
                                    py={1}
                                    borderRadius="md"
                                >
                                    {daysUntilStart === 0 ? 'Starts Today' : 
                                     daysUntilStart === 1 ? 'Starts Tomorrow' :
                                     `In ${daysUntilStart} days`}
                                </Badge>
                            )}
                        </HStack>

                        {/* Module name */}
                        <Text 
                            fontSize={{base: "sm", md: "md"}} 
                            fontWeight="700" 
                            color="gray.800"
                            lineHeight="1.4"
                            mb={1}
                        >
                            {course_name}
                        </Text>

                        {/* Date range */}
                        <Box 
                            p={{base: 2, md: 2.5}} 
                            bg="gray.50" 
                            borderRadius="md"
                            borderLeft="3px solid"
                            borderColor="brand.400"
                        >
                            <VStack align="stretch" spacing={1}>
                                <HStack spacing={2}>
                                    <Text fontSize={{base: "10px", md: "xs"}} fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.5px">
                                        Start:
                                    </Text>
                                    <Text fontSize={{base: "xs", md: "sm"}} fontWeight="600" color="gray.800">
                                        {startDateFormatted}
                                    </Text>
                                </HStack>
                                <HStack spacing={2}>
                                    <Text fontSize={{base: "10px", md: "xs"}} fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="0.5px">
                                        End:
                                    </Text>
                                    <Text fontSize={{base: "xs", md: "sm"}} fontWeight="600" color="gray.800">
                                        {endDateFormatted}
                                    </Text>
                                </HStack>
                            </VStack>
                        </Box>

                        {/* Click hint */}
                        <Box mt="auto" pt={2}>
                            <Text fontSize={{base: "10px", md: "xs"}} color="gray.500" textAlign="center" fontStyle="italic">
                                Click to view schedule
                            </Text>
                        </Box>
                    </VStack>
                </Box>

                {/* Back of card - Schedule */}
                <Box
                    ref={backCardRef}
                    position="absolute"
                    width="100%"
                    top="0"
                    left="0"
                    borderWidth='1px'
                    borderRadius='xl'
                    boxShadow='sm'
                    borderColor="gray.200"
                    bg="white"
                    px={{base: 3, md: 4}}
                    py={{base: 3, md: 4}}
                    wordBreak="break-word"
                    display="flex"
                    flexDirection="column"
                    overflow="auto"
                    sx={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)'
                    }}
                >
                    {/* Accent bar at top */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height="4px"
                        bgGradient="linear(to-r, brand.400, brand.600)"
                    />
                    
                    <VStack align="stretch" spacing={3} flex={1}>
                        <HStack justify="space-between" align="center" flexWrap="wrap" spacing={2}>
                            <Text fontSize={{base: "xs", md: "sm"}} fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.5px">
                                Schedule
                            </Text>
                            <Badge 
                                colorScheme="blue" 
                                fontSize={{base: "10px", md: "xs"}} 
                                px={{base: 2, md: 2.5}} 
                                py={1} 
                                borderRadius="md"
                                fontWeight="600"
                                textTransform="uppercase"
                            >
                                {course_type_code}
                            </Badge>
                        </HStack>

                        {Object.keys(datesByMonth).length > 0 ? (
                            <VStack spacing={3} align="stretch">
                                {Object.values(datesByMonth).map((month, index) => (
                                    <Box key={index}>
                                        <Text fontSize={{base: "10px", md: "xs"}} fontWeight="700" color="gray.700" mb={2} textTransform="uppercase" letterSpacing="0.5px">
                                            {month.monthName}
                                        </Text>

                                        <Box overflowX="auto" borderRadius="md" border="1px solid" borderColor="gray.200" w="100%">
                                            <Table variant="simple" size="sm" colorScheme="gray" minW="200px">
                                                <Thead bg="gray.50">
                                                    <Tr>
                                                        <Th fontSize={{base: "10px", md: "xs"}} fontWeight="700" color="gray.700" p={{base: 2, md: 2.5}} borderBottom="1px solid" borderColor="gray.200">Date</Th>
                                                        <Th fontSize={{base: "10px", md: "xs"}} fontWeight="700" color="gray.700" p={{base: 2, md: 2.5}} borderBottom="1px solid" borderColor="gray.200">Time</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {month.dates.map((date, dateIndex) => {
                                                        const startTimeFormatted = moment(date.startTime, "HHmm").format("h:mm A");
                                                        const endTimeFormatted = moment(date.endTime, "HHmm").format("h:mm A");
                                                        const timeRange = `${startTimeFormatted} - ${endTimeFormatted}`;
                                                        
                                                        return (
                                                            <Tr key={dateIndex} _hover={{ bg: "blue.50" }} transition="background 0.2s">
                                                                <Td fontSize={{base: "10px", md: "xs"}} p={{base: 2, md: 2.5}} borderBottom="1px solid" borderColor="gray.100">
                                                                    <VStack spacing={0.5} align="start">
                                                                        <Text fontWeight="700" fontSize={{base: "xs", md: "sm"}} color="gray.800">{date.day}</Text>
                                                                        <Text fontSize={{base: "9px", md: "10px"}} color="gray.600" textTransform="capitalize" fontWeight="500">
                                                                            {date.dayOfWeek}
                                                                        </Text>
                                                                    </VStack>
                                                                </Td>
                                                                <Td fontSize={{base: "10px", md: "xs"}} p={{base: 2, md: 2.5}} borderBottom="1px solid" borderColor="gray.100">
                                                                    <Text fontWeight="600" fontSize={{base: "10px", md: "xs"}} color="gray.800">
                                                                        {timeRange}
                                                                    </Text>
                                                                </Td>
                                                            </Tr>
                                                        );
                                                    })}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </Box>
                                ))}
                            </VStack>
                            ) : (
                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.500" textAlign="center" py={4}>
                                    No schedule available
                                </Text>
                            )}

                        {/* Click hint */}
                        <Box mt="auto" pt={2}>
                            <Text fontSize={{base: "10px", md: "xs"}} color="gray.500" textAlign="center" fontStyle="italic">
                                Click to return
                            </Text>
                        </Box>
                    </VStack>
                </Box>
            </Box>
        </Box>
    );
}

const FullCertRegistration = () => {
    const [courseRuns, setCourseRuns] = useState([]);
    const [groupedCourseRuns, setGroupedCourseRuns] = useState({});
    const [loadingCourseRuns, setLoadingCourseRuns] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [fullCertificationCost, setFullCertificationCost] = useState(null);
    const [selectedCourseRunIds, setSelectedCourseRunIds] = useState([]);
    const [registrationId, setRegistrationId] = useState(null);
    const [isInitialSaveComplete, setIsInitialSaveComplete] = useState(false);
    const [loadingRegistration, setLoadingRegistration] = useState(false);
    const [additionalFields, setAdditionalFields] = useState([]);
    const [basicFieldsOrder, setBasicFieldsOrder] = useState([]);
    const [compulsoryCustomFields, setCompulsoryCustomFields] = useState([]);
    const [selectedCertName, setSelectedCertName] = useState('');
    const [showModules, setShowModules] = useState(false);
    
    // Organization GST settings
    const [orgGstSettings, setOrgGstSettings] = useState({
        gst_enabled: false,
        gst_rate: 0
    });
    const payNowOrg = useOrganizationPayNowDetails();
    
    // File upload state for compulsory fields
    const [selectedFiles, setSelectedFiles] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState(new Set());

    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const { fullCertRegistrationPath } = useRouteConfig();
    const fullCertificationId = searchParams.get('full_certification');
    console.log(fullCertificationId, "fullCertificationId")

    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const additionalFieldsRef = useRef(null);
    const hasShownLoadToast = useRef(false);

    const [formData, setFormData] = useState({
        participant_name: '',
        participant_email: '',
        nationality: '',
        taking_course_in_singapore: '',
        birth_age: '',
        nric: '',
        mobile_no: '',
        mobile_no_raw: '',
        dob: '',
        displayDob: '',
        draft_cert_run_code: '',
        full_certification_id: '',
        payment_option: 'pay_now', // Default to pay now
    });
    
    // Progress calculation moved after state definitions - see below

    // -----------------------
    // Local draft (localStorage)
    // -----------------------
    const LOCAL_DRAFT_PREFIX = 'full_cert_registration_draft:';
    const localDraftKey = id ? `${LOCAL_DRAFT_PREFIX}${id}` : null;

    const sanitizeFormDataForStorage = (data) => {
        // localStorage has size limits; do not store large blobs (e.g., signatures) or non-serializable values.
        const cleaned = {};
        Object.entries(data || {}).forEach(([k, v]) => {
            if (k.toLowerCase().includes('signature')) return; // skip signature base64
            if (k === 'registration_stage') return; // server is the source of truth
            if (v instanceof File) return;
            if (Array.isArray(v) && v.some((x) => x instanceof File)) return;
            if (typeof v === 'string' && v.length > 200000) return; // safety guard
            cleaned[k] = v;
        });
        return cleaned;
    };

    const readLocalDraft = () => {
        if (!localDraftKey) return null;
        try {
            const raw = window.localStorage.getItem(localDraftKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed || null;
        } catch {
            return null;
        }
    };

    const writeLocalDraft = (data) => {
        if (!localDraftKey) return;
        try {
            window.localStorage.setItem(
                localDraftKey,
                JSON.stringify({ ts: Date.now(), formData: sanitizeFormDataForStorage(data) })
            );
        } catch (e) {
            // Quota exceeded or blocked storage; fail silently.
            devLog('localStorage write failed', e);
        }
    };

    const clearLocalDraft = () => {
        if (!localDraftKey) return;
        try {
            window.localStorage.removeItem(localDraftKey);
        } catch {
            // ignore
        }
    };

    const [validationErrors, setValidationErrors] = useState({
        email: false,
        mobile: false,
        invalidDomain: false,
        dob: false,
        nric: false,
    });
    
    // Track which fields have been touched (focused and blurred) - for on-blur validation
    const [touchedFields, setTouchedFields] = useState(new Set());
    
    // Helper to mark field as touched
    const markFieldTouched = (fieldKey) => {
        setTouchedFields(prev => new Set(prev).add(fieldKey));
    };

    // Signature pad and agreement state
    const signaturePadRef = useRef(null);
    const signatureContainerRef = useRef(null);
    const signatureDataUrlRef = useRef(null); // Persist signature across orientation/resize
    const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
    const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
    
    // PayNow QR Modal state
    const [showPayNowModal, setShowPayNowModal] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [stripeEnabled, setStripeEnabled] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchIsStripePaymentConfigured(supabase).then((enabled) => {
            if (cancelled) return;
            setStripeEnabled(enabled);
            if (!enabled) {
                setFormData((prev) =>
                    prev.payment_option === 'stripe'
                        ? { ...prev, payment_option: 'pay_now' }
                        : prev
                );
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleStripePayment = async () => {
        setIsProcessingPayment(true);
        try {
            // Fetch the invoice for this registration
            const { data: invoices, error: invoiceError } = await supabase
                .from('invoices')
                .select('id, invoice_line_items(*)')
                .eq('registration_id', registrationId)
                .eq('invoice_type', 'registration fee')
                .single();

            if (invoiceError) throw invoiceError;

            // Calculate invoice balance from line items (or fetch registration_fee from full_certification)
            // Null/unset registration_fee → 0 (do not invent $100)
            let invoiceBalance = 0;
            if (invoices.invoice_line_items && invoices.invoice_line_items.length > 0) {
                // Use the sum of line items if available
                invoiceBalance = invoices.invoice_line_items.reduce((sum, item) => {
                    return sum + (parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 1));
                }, 0);
            } else if (formData.draft_cert_run_code) {
                // Fetch registration_fee from full_certification table
                try {
                    const { data: certRunData, error: certRunError } = await supabase
                        .from('full_certification_runs')
                        .select('full_cert_type_code, full_certification(registration_fee)')
                        .eq('id', formData.draft_cert_run_code)
                        .single();
                    
                    const fee = certRunData?.full_certification?.registration_fee;
                    if (!certRunError && fee !== null && fee !== undefined && fee !== '') {
                        const parsed = parseFloat(fee);
                        invoiceBalance = Number.isNaN(parsed) ? 0 : parsed;
                    }
                } catch (error) {
                    console.error('Error fetching registration_fee:', error);
                }
            }

            // Apply GST when org GST is on, unless location-based GST exemption applies
            const applyGst = shouldApplyGst({
                orgGstEnabled: !!orgGstSettings.gst_enabled,
                locationBasedGst: selectedCertRun?.full_certification?.location_based_gst === true,
                nationality: formData.nationality,
                takingCourseInSingapore: formData.taking_course_in_singapore,
            });
            const gstRate = applyGst ? (orgGstSettings.gst_rate / 100) : 0;
            const gstAmount = invoiceBalance * gstRate;
            const totalWithGst = invoiceBalance + gstAmount;

            // Prepared data for the edge function - send total including GST (if any)
            const requestData = {
                registration_id: registrationId,
                participant_email: formData.participant_email,
                participant_name: formData.participant_name,
                invoice_id: invoices.id,
                invoice_balance: totalWithGst, // Total including GST
                registration_fee_subtotal: invoiceBalance, // Pre-GST amount for reference
                gst_amount: gstAmount, // GST amount for reference
                gst_rate: orgGstSettings.gst_rate, // GST rate for reference
                gst_enabled: applyGst, // Effective GST (may be off for overseas e-learning)
                course_name: selectedCertType?.name || 'Full Certification Registration',
                course_start_date: selectedCertRun?.start_date 
                    ? moment(selectedCertRun.start_date).format('DD MMM YYYY') 
                    : null
            };

            const { data, error } = await supabase.functions.invoke("create-registration-checkout", {
                body: requestData
            });

            if (error) throw error;
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error("Checkout URL not found");
            }
        } catch (err) {
            console.error('Error with Stripe checkout:', err);
            toast({
                title: "Payment Error",
                description: "Could not initiate Stripe payment. Please try again or use PayNow.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsProcessingPayment(false);
        }
    };
    
    // Verification banner ref for scrolling
    // verificationBannerRef removed - email verification now shown in floating progress bar

    // Email verification state
    const [emailVerified, setEmailVerified] = useState(false);
    const [isSendingVerification, setIsSendingVerification] = useState(false);
    const [emailVerificationSent, setEmailVerificationSent] = useState(false);
    const [originalEmail, setOriginalEmail] = useState(''); // Track email changes

    // progressData will be calculated after all helper functions are defined (see below)

    const handlePayNowDone = async () => {
        setIsUpdatingStatus(true);
        try {
            // Update registration status to 'payment_in_progress'
            const { error: updateError } = await supabase
                .from('registration')
                .update({ registration_stage: 'payment_in_progress' })
                .eq('id', registrationId);

            if (updateError) throw updateError;

            toast({
                title: "Payment Recorded",
                    description: "Your registration status has been updated to 'payment in progress'. We will verify the payment shortly.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            // Redirect to pending page
            navigate(`/registration-pending/${registrationId}`);
        } catch (err) {
            console.error('Error updating status:', err);
            toast({
                title: "Error",
                description: "Could not update your registration status. Please contact support.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsUpdatingStatus(false);
            setShowPayNowModal(false);
        }
    };

    // Resize signature pad canvas to match container for proper coordinate handling
    // Skip when form is read-only — pad is not mounted; saved signature is shown as an image instead
    useEffect(() => {
        if (!isInitialSaveComplete) return;

        const stage = formData.registration_stage;
        const formIsReadOnly = !!stage && stage !== "draft" && stage !== "rejected";
        if (formIsReadOnly) return;

        const resizeCanvas = () => {
            if (signaturePadRef.current && signatureContainerRef.current) {
                const canvas = signaturePadRef.current.getCanvas();
                const container = signatureContainerRef.current;
                const rect = container.getBoundingClientRect();
                
                // Get computed styles to account for padding and border
                const computedStyle = window.getComputedStyle(container);
                const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
                const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
                const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0;
                const borderRight = parseFloat(computedStyle.borderRightWidth) || 0;
                
                // Calculate available width accounting for padding and borders
                const availableWidth = Math.max(
                    rect.width - paddingLeft - paddingRight - borderLeft - borderRight,
                    300 // Minimum 300px
                );
                const height = 200;
                
                // Get device pixel ratio for crisp rendering
                const dpr = window.devicePixelRatio || 1;
                
                // Restore from persisted ref (canvas may already be cleared by resize race)
                const existingSignature = signatureDataUrlRef.current;
                
                // Set canvas internal dimensions (actual pixel size)
                canvas.width = availableWidth * dpr;
                canvas.height = height * dpr;
                
                // Set canvas display size (CSS size) - this is what the user sees
                // Use max-width to ensure it doesn't overflow
                canvas.style.width = `${availableWidth}px`;
                canvas.style.height = `${height}px`;
                canvas.style.maxWidth = '100%';
                canvas.style.boxSizing = 'border-box';
                
                // Scale the context to account for device pixel ratio
                const ctx = canvas.getContext('2d');
                ctx.scale(dpr, dpr);
                
                // Restore signature if it existed and sync React state
                if (existingSignature) {
                    signaturePadRef.current.fromDataURL(existingSignature);
                    setIsSignatureEmpty(false);
                } else {
                    setIsSignatureEmpty(true);
                }
            }
        };

        // Small delay to ensure container is rendered and measured correctly
        const timeoutId = setTimeout(() => {
            resizeCanvas();
        }, 150);

        // Resize on window resize with debounce
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(resizeCanvas, 100);
        };
        
        window.addEventListener('resize', handleResize);
        
        // Also resize when container size changes
        const observer = new ResizeObserver(() => {
            resizeCanvas();
        });
        
        if (signatureContainerRef.current) {
            observer.observe(signatureContainerRef.current);
        }

        return () => {
            clearTimeout(timeoutId);
            clearTimeout(resizeTimeout);
            window.removeEventListener('resize', handleResize);
            observer.disconnect();
        };
    }, [isInitialSaveComplete, formData.registration_stage]);

    const getAllCourseRuns = async () => {
        setLoadingCourseRuns(true);
        let basequery = supabase.from("full_certification_runs")
        .select("*, full_cert_many_course_run(*, course_run(*, course_type(*))), full_certification(*)")
            .eq("is_active", true)
            .order("start_date");

        if (fullCertificationId) {
            // full_certification_runs table uses full_cert_type_code to reference full_certification.code
            basequery = basequery.eq('full_cert_type_code', fullCertificationId);
        }

        const {data, error} = await basequery;

        if (error) {
            console.error("Error fetching course runs:", error);
            setLoadingCourseRuns(false);
            return;
        }

        // Filter runs that are still accepting registrations
        // Show runs where registration_closing_date >= today OR (registration_closing_date is null AND start_date >= today)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const filteredData = (data || []).filter(certRun => {
            // If registration_closing_date exists, use it
            if (certRun.registration_closing_date) {
                const closingDate = new Date(certRun.registration_closing_date);
                closingDate.setHours(0, 0, 0, 0);
                return closingDate >= today;
            }
            // Otherwise, fall back to start_date
            if (certRun.start_date) {
                const startDate = new Date(certRun.start_date);
                startDate.setHours(0, 0, 0, 0);
                return startDate >= today;
            }
            // If neither exists, include it (shouldn't happen but be safe)
            return true;
        });

        // Extract all course_run IDs from the nested structure
        const courseRunIds = [];
        filteredData.forEach(certRun => {
            if (certRun.full_cert_many_course_run) {
                certRun.full_cert_many_course_run.forEach(link => {
                    if (link.course_run && link.course_run.id) {
                        courseRunIds.push(link.course_run.id);
                    }
                });
            }
        });

        // Fetch all sessions for these course runs
        let sessionsData = [];
        if (courseRunIds.length > 0) {
            const { data: allSessions, error: sessionsError } = await supabase
                .from("session")
                .select("*")
                .in("course_run_code", courseRunIds)
                .order("session_date", { ascending: true });

            if (sessionsError) {
                console.error("Error fetching sessions:", sessionsError);
            } else {
                sessionsData = allSessions || [];
            }
        }

        // Group sessions by course_run_code
        const sessionsByCourseRun = {};
        sessionsData.forEach(session => {
            if (!sessionsByCourseRun[session.course_run_code]) {
                sessionsByCourseRun[session.course_run_code] = [];
            }
            sessionsByCourseRun[session.course_run_code].push(session);
        });

        // Transform data to include course_dates from sessions
        const transformedData = (data || []).map(certRun => {
            // Process nested course_run data
            if (certRun.full_cert_many_course_run) {
                certRun.full_cert_many_course_run = certRun.full_cert_many_course_run.map(link => {
                    if (link.course_run) {
                        const sessions = sessionsByCourseRun[link.course_run.id] || [];
                        const course_dates = sessions.map(session => ({
                            date: session.session_date,
                            start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
                            end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
                            room: session.custom_room || session.room_id,
                            venue: session.custom_venue || session.venue_id
                        }));
                        link.course_run.course_dates = course_dates;
                    }
                    return link;
                });
            }
            return certRun;
        });

        // Group by certification type and sort by start_date
        const groupedByCertType = transformedData.reduce((acc, certRun) => {
            const certTypeKey = certRun.full_certification?.code || certRun.full_cert_type_code || 'unknown';
            const certTypeName = certRun.full_certification?.name || 'Unknown Certification';
            
            if (!acc[certTypeKey]) {
                acc[certTypeKey] = {
                    name: certTypeName,
                    runs: []
                };
            }
            
            acc[certTypeKey].runs.push(certRun);
            return acc;
        }, {});

        // Sort each group's runs by start_date ascending
        Object.keys(groupedByCertType).forEach(certTypeKey => {
            groupedByCertType[certTypeKey].runs.sort((a, b) => {
                const dateA = new Date(a.start_date);
                const dateB = new Date(b.start_date);
                return dateA - dateB;
            });
            
            // Recent: still show a run until 7 days after its start date.
            // Future: next 2 start dates only. If several runs share a start date, show all of them.
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const oneWeekAgo = new Date(today);
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

            const recentRuns = [];
            const futureByStartDate = new Map();

            groupedByCertType[certTypeKey].runs.forEach(run => {
                if (!run.start_date) return;

                const startDate = new Date(run.start_date);
                startDate.setHours(0, 0, 0, 0);
                const startKey = `${startDate.getFullYear()}-${startDate.getMonth()}-${startDate.getDate()}`;

                if (startDate <= today && startDate >= oneWeekAgo) {
                    recentRuns.push(run);
                } else if (startDate > today) {
                    if (!futureByStartDate.has(startKey)) {
                        futureByStartDate.set(startKey, []);
                    }
                    futureByStartDate.get(startKey).push(run);
                }
            });

            const futureRuns = [];
            let futureDateCount = 0;
            for (const runsOnDate of futureByStartDate.values()) {
                if (futureDateCount >= 2) break;
                futureRuns.push(...runsOnDate);
                futureDateCount += 1;
            }

            groupedByCertType[certTypeKey].runs = [...recentRuns, ...futureRuns];
        });

        // Store both the grouped structure and flat list for backward compatibility
        setCourseRuns(transformedData);
        setGroupedCourseRuns(groupedByCertType);
        devLog("Fetched course run data:", transformedData);
        devLog("Grouped course runs:", groupedByCertType);
        setLoadingCourseRuns(false);
    };

    // Load existing registration if ID is in URL (and merge locally-saved draft so refresh won't wipe Step 2)
    useEffect(() => {
        // Reset toast flag when ID changes
        hasShownLoadToast.current = false;

        const loadRegistration = async () => {
            if (!id) return;
            setLoadingRegistration(true);
            try {
                const {data, error} = await supabase
                    .from('registration')
                    .select('*')
                    .eq('id', id)
                    .single();

                if (error) throw error;

                if (data) {
                    // Populate form with existing data including additional_data
                        const baseFormData = {
                        participant_name: data.participant_name || '',
                        participant_email: data.participant_email || '',
                        nationality: data.nationality || '',
                        birth_age: data.birth_age || '',
                        nric: data.nric || '',
                        mobile_no: data.mobile_no || '',
                        mobile_no_raw: data.mobile_no || '',
                        dob: data.dob || '',
                        displayDob: data.dob || '',
                        draft_cert_run_code: data.draft_cert_run_code || '',
                        rejection_reason: data.rejection_reason || '',
                        registration_stage: data.registration_stage || '',
                    };

                    const mergedFormData = data.additional_data
                        ? { ...baseFormData, ...data.additional_data }
                        : baseFormData;

                    const localDraft = readLocalDraft();

                    // If TMS has moved the registration past draft, never let a stale local draft override server state.
                    const serverStage = data.registration_stage || '';
                    const isServerPastDraft = serverStage && serverStage !== 'draft';

                    let mergedWithLocalDraft = mergedFormData;
                    if (localDraft?.formData && !isServerPastDraft) {
                        // Only apply local draft while still in draft stage
                        mergedWithLocalDraft = { ...mergedFormData, ...localDraft.formData };
                    } else if (localDraft?.formData && isServerPastDraft) {
                        // Clear stale local draft so it won't keep overriding on future refreshes
                        clearLocalDraft();
                    }

                    // Always trust server for registration stage
                    mergedWithLocalDraft.registration_stage = serverStage;

                    // Ensure we always have a default selection even for old registrations
                    if (!mergedWithLocalDraft.payment_option) mergedWithLocalDraft.payment_option = 'pay_now';

                    setFormData(mergedWithLocalDraft);
                    setRegistrationId(id);
                    setIsInitialSaveComplete(true);

                    // Load email verification status
                    setEmailVerified(data.email_verified || false);
                    setEmailVerificationSent(!!data.email_verification_sent_at);
                    setOriginalEmail(data.participant_email || '');

                    if (!hasShownLoadToast.current) {
                        hasShownLoadToast.current = true;
                        setTimeout(() => {
                            toast({
                                title: "Registration Loaded",
                                description: "Your saved registration has been loaded.",
                                status: "info",
                                duration: 2000,
                                isClosable: true,
                            });
                        }, 100);
                    }
                }
            } catch (err) {
                console.error('Error loading registration:', err);
                toast({
                    title: "Error Loading Registration",
                    description: "Could not load the saved registration. Please try again.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setLoadingRegistration(false);
            }
        };

        loadRegistration();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Debounced auto-save to localStorage (so refresh keeps Step 2 inputs)
    useEffect(() => {
        if (!localDraftKey) return;
        if (!(formData.participant_name || formData.participant_email || formData.nric)) return;

        const t = setTimeout(() => {
            writeLocalDraft(formData);
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localDraftKey, formData]);

    // Fetch course runs from database
    useEffect(() => {
        getAllCourseRuns();
    }, []);

    // Fetch organization GST settings
    useEffect(() => {
        const fetchOrganizationGstSettings = async () => {
            try {
                const { data, error } = await supabase
                    .from('organization')
                    .select('gst_enabled, gst_rate')
                    .eq('status', 'active')
                    .single();
                
                if (error) {
                    console.error('Error fetching organization GST settings:', error);
                    return;
                }
                
                if (data) {
                    setOrgGstSettings({
                        gst_enabled: data.gst_enabled || false,
                        gst_rate: parseFloat(data.gst_rate) || 0
                    });
                    devLog('Organization GST settings loaded:', data);
                }
            } catch (err) {
                console.error('Error fetching organization GST settings:', err);
            }
        };
        
        fetchOrganizationGstSettings();
    }, []);

    // Initialize selectedCertName when course runs are loaded or when cert run is already selected
    useEffect(() => {
        if (Object.keys(groupedCourseRuns).length > 0) {
            // If fullCertificationId is in URL params, pre-select it
            if (fullCertificationId && !selectedCertName) {
                // Check if the fullCertificationId matches any cert type key
                const matchingCertKey = Object.keys(groupedCourseRuns).find(
                    key => key === fullCertificationId
                );
                if (matchingCertKey) {
                    setSelectedCertName(matchingCertKey);
                }
            }
            
            // If draft_cert_run_code is already set, find the corresponding certification
            if (formData.draft_cert_run_code && !selectedCertName) {
                const selectedRun = courseRuns.find(c => c.id === formData.draft_cert_run_code);
                if (selectedRun) {
                    const certKey = selectedRun.full_certification?.code || selectedRun.full_cert_type_code;
                    if (certKey && groupedCourseRuns[certKey]) {
                        setSelectedCertName(certKey);
                    }
                }
            }
        }
    }, [groupedCourseRuns, courseRuns, fullCertificationId, formData.draft_cert_run_code]);

    // Fetch full certification cost
    useEffect(() => {
        const fetchFullCertificationCost = async () => {
            const params = new URLSearchParams(location.search);
            const fullCertificationId = params.get('full_certification');

            if (fullCertificationId && !fullCertificationCost) {
                try {
                    const {data, error} = await supabase
                        .from('full_certification')
                        .select('cost')
                        .eq('code', fullCertificationId)
                        .single();

                    if (error) throw error;
                    setFullCertificationCost(parseFloat(data.cost || 0));
                } catch (err) {
                    console.error('Error fetching full certification cost:', err);
                }
            }
        };

        fetchFullCertificationCost();
    }, [location.search, fullCertificationCost]);

    // Auto-select all modules when certification run is selected
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fullCertification = params.get('full_certification');

        if (fullCertification && formData.certification_run_id && courseRuns.length > 0) {
            // courseRuns contains full_certification_runs records, which have full_cert_type_code referencing full_certification.code
            const certificationCourses = courseRuns.filter(course =>
                (course.full_cert_type_code === fullCertification || course.full_certification?.code === fullCertification) &&
                course.certification_run_id === formData.certification_run_id &&
                course.is_full_certification === true
            );

            devLog('Found certification courses:', certificationCourses.length, certificationCourses);

            if (certificationCourses.length > 0) {
                const allCourseRunIds = certificationCourses.map(course => course.id);
                setSelectedCourseRunIds(allCourseRunIds);
                devLog('Auto-selected course run IDs:', allCourseRunIds);
            }
        }
    }, [formData.certification_run_id, courseRuns, location.search]);

    // Fetch additional fields for validation
    useEffect(() => {
        const fetchAdditionalFields = async () => {
            try {
                const { data, error } = await supabase
                    .from('additional_registration_fields')
                    .select('field_key, is_required, field_label, conditional_requirements, field_type')
                    .or('field_status.is.null,field_status.eq.active');
                
                if (error) throw error;
                setAdditionalFields(data || []);
            } catch (err) {
                console.error('Error fetching additional fields:', err);
            }
        };
        
        fetchAdditionalFields();
    }, []);

    // Fetch basic fields order from database (system fields)
    useEffect(() => {
        const fetchBasicFieldsOrder = async () => {
            try {
                const { data, error } = await supabase
                    .from('additional_registration_fields')
                    .select('field_key, field_label, display_order, field_behaviors, field_options')
                    .or('field_status.is.null,field_status.eq.active')
                    .eq('is_original_field', true)
                    .order('display_order', { ascending: true });
                
                if (error) throw error;
                
                // Parse field_behaviors if it's a string
                const processedData = (data || []).map(field => {
                    let fieldBehaviors = field.field_behaviors;
                    if (typeof fieldBehaviors === 'string') {
                        try {
                            fieldBehaviors = JSON.parse(fieldBehaviors);
                        } catch (e) {
                            console.warn('Failed to parse field_behaviors for field:', field.field_key, e);
                            fieldBehaviors = null;
                        }
                    }
                    return {
                        ...field,
                        field_behaviors: fieldBehaviors
                    };
                });
                
                setBasicFieldsOrder(processedData);
            } catch (err) {
                console.error('Error fetching basic fields order:', err);
            }
        };
        
        fetchBasicFieldsOrder();
    }, []);

    // Fetch compulsory custom fields (custom fields in Compulsory fields section) for Step 1
    useEffect(() => {
        const fetchCompulsoryCustomFields = async () => {
            try {
                const { data, error } = await supabase
                    .from('additional_registration_fields')
                    .select('*')
                    .or('field_status.is.null,field_status.eq.active')
                    .eq('is_original_field', false) // Only custom fields
                    .eq('section', 'Compulsory fields')
                    .order('display_order', { ascending: true });
                
                if (error) throw error;
                
                // Parse field_behaviors if it's a string
                const processedData = (data || []).map(field => {
                    let fieldBehaviors = field.field_behaviors;
                    if (typeof fieldBehaviors === 'string') {
                        try {
                            fieldBehaviors = JSON.parse(fieldBehaviors);
                        } catch (e) {
                            console.warn('Failed to parse field_behaviors for field:', field.field_key, e);
                            fieldBehaviors = null;
                        }
                    }
                    return {
                        ...field,
                        field_behaviors: fieldBehaviors
                    };
                });
                
                setCompulsoryCustomFields(processedData);
            } catch (err) {
                console.error('Error fetching compulsory custom fields:', err);
            }
        };
        
        fetchCompulsoryCustomFields();
    }, []);

    // Set document title
    useEffect(() => {
        applyTenantDocumentTitle("Full Certification Registration");
        return () => {
            applyTenantDocumentTitle();
        };
    }, []);

    // Helper function to get dynamic label based on field_behaviors
    const getDynamicLabel = (fieldKey, defaultLabel) => {
        // Find the field in basicFieldsOrder or compulsoryCustomFields
        const field = basicFieldsOrder.find(f => f.field_key === fieldKey) || 
                     compulsoryCustomFields.find(f => f.field_key === fieldKey);
        if (!field?.field_behaviors?.dynamic_label) {
            return defaultLabel;
        }

        const { trigger_field, mappings } = field.field_behaviors.dynamic_label;
        const triggerValue = formData[trigger_field] || '';
        
        return mappings[triggerValue] || mappings.default || defaultLabel;
    };

    // Helper function to get dynamic label for a field object
    const getFieldDynamicLabel = (field) => {
        if (!field?.field_behaviors?.dynamic_label) {
            return field.field_label;
        }

        const { trigger_field, mappings } = field.field_behaviors.dynamic_label;
        const triggerValue = getFieldValue(formData, trigger_field) || '';
        
        return mappings[triggerValue] || mappings.default || field.field_label;
    };

    // Helper function to get placeholder (supports both static and dynamic)
    const getFieldPlaceholder = (field) => {
        if (!field || !field.field_behaviors) {
            return '';
        }
        
        // First check for static custom placeholder
        if (field.field_behaviors.custom_placeholder) {
            return field.field_behaviors.custom_placeholder;
        }
        
        // Then check for dynamic placeholder from dynamic_label.placeholders
        if (field.field_behaviors.dynamic_label?.placeholders) {
            const { trigger_field, placeholders } = field.field_behaviors.dynamic_label;
            const triggerValue = getFieldValue(formData, trigger_field) || '';
            
            // Try to get placeholder for current trigger value, fallback to default
            const placeholder = placeholders[triggerValue] || placeholders.default || '';
            return placeholder;
        }
        
        // Fallback to empty string (no placeholder)
        return '';
    };

    // REMOVED: Scroll-into-view for verification banner - it was jarring on mobile
    // Instead, we now show a fixed notification toast and the banner stays in its natural position
    // The user can continue filling the form without being forcibly scrolled

    const handleChange = (name, value) => {
        setFormData((prevData) => {
            const next = {
                ...prevData,
                [name]: value,
            };
            // Only passport students answer the overseas question; clear stale answers otherwise
            if (name === 'nationality' && !isNonSingaporeResidentPassport(value)) {
                next.taking_course_in_singapore = '';
            }
            return next;
        });
    };

    const isSelectedCertLocationBasedGst = () => {
        const run = courseRuns.find((c) => c.id === formData.draft_cert_run_code);
        return run?.full_certification?.location_based_gst === true;
    };

    const getEffectiveGstEnabled = () =>
        shouldApplyGst({
            orgGstEnabled: !!orgGstSettings.gst_enabled,
            locationBasedGst: isSelectedCertLocationBasedGst(),
            nationality: formData.nationality,
            takingCourseInSingapore: formData.taking_course_in_singapore,
        });

    // Helper function to check if nationality requires passport (supports both old and new values)
    const requiresPassport = (nationality) => {
        if (!nationality) return false;
        return nationality === "Non-Singapore Resident (Foreign Passport)" ||
               nationality === "Non-Singapore Resident (Passport No)" ||
               nationality === "Non-Singapore Resident( Passport No)";
    };

    // Helper function to check if nationality requires FIN (backend values only; frontend maps display→backend)
    const requiresFIN = (nationality) => {
        if (!nationality) return false;
        return nationality === "Work Pass/ E-Pass / LTVP (FIN)" ||
               nationality === "Non-Singapore Citizen/PR (FIN)" ||
               nationality === "Long Term Visitor Pass+ (LTVP+)";
    };

    const getSelectedCourseDetails = () => {
        if (!selectedCertName || !formData.draft_cert_run_code) return null;

        const courseName = groupedCourseRuns[selectedCertName]?.name;
        const run = courseRuns.find(c => c.id === formData.draft_cert_run_code);
        if (!courseName || !run) return null;

        return {
            courseName,
            batchName: run.intake ? `Batch ${run.intake}` : null,
            startDate: run.start_date ? moment(run.start_date).format('D MMM, YYYY') : null,
            endDate: run.end_date ? moment(run.end_date).format('D MMM, YYYY') : null,
        };
    };

    // Render basic field based on field_key
    const renderBasicField = (fieldKey) => {
        // Find the field configuration from basicFieldsOrder
        const fieldConfig = basicFieldsOrder.find(f => f.field_key === fieldKey);
        
        const fieldMap = {
            'nationality': (
                <React.Fragment key="nationality-block">
                    <FormControl mb={{base: 4, md: 5}}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldConfig?.field_label || "Identification Type"}
                            <Text as="span" color="red.500" ml={1}>*</Text>
                        </FormLabel>
                        {(() => {
                            const nationalityOptions = (fieldConfig?.field_options && fieldConfig.field_options.length > 0) ? fieldConfig.field_options : DEFAULT_NATIONALITY_OPTION_VALUES;
                            const optionLabels = fieldConfig?.field_behaviors?.option_labels ?? {};
                            const getDisplayLabel = (value) => optionLabels[value] ?? value;

                            if (nationalityOptions.length > 0 && nationalityOptions.length < 5) {
                                return (
                                    <SimpleGrid 
                                        columns={{ 
                                            base: 1, 
                                            sm: nationalityOptions.length === 2 ? 2 : (nationalityOptions.length === 3 ? 3 : 2),
                                            md: Math.min(nationalityOptions.length, 3)
                                        }} 
                                        spacing={2}
                                    >
                                        {nationalityOptions.map((value) => {
                                            const displayLabel = getDisplayLabel(value);
                                            const isSelected = formData.nationality === value;
                                            return (
                                                <Button
                                                    key={value}
                                                    onClick={() => handleChange("nationality", value)}
                                                    colorScheme={isSelected ? "blue" : "gray"}
                                                    variant={isSelected ? "solid" : "outline"}
                                                    isDisabled={isReadOnly}
                                                    size="md"
                                                    py={{ base: 4, md: 5 }}
                                                    px={4}
                                                    whiteSpace="normal"
                                                    height="auto"
                                                    textAlign="center"
                                                    fontWeight={isSelected ? "bold" : "medium"}
                                                    borderColor={isSelected ? "blue.500" : "gray.300"}
                                                    _hover={!isReadOnly ? {
                                                        bg: isSelected ? "blue.600" : "gray.100",
                                                        borderColor: "blue.500"
                                                    } : {}}
                                                >
                                                    {displayLabel}
                                                </Button>
                                            );
                                        })}
                                    </SimpleGrid>
                                );
                            }

                            return (
                                <Select
                                    value={formData.nationality}
                                    placeholder='Select option'
                                    bg={isReadOnly ? "gray.50" : "white"}
                                    onChange={(e) => handleChange("nationality", e.target.value)}
                                    isDisabled={isReadOnly}
                                    size={{base: "md", md: "md"}}
                                    height={{base: "48px", md: "40px"}}
                                >
                                    {nationalityOptions.map((value) => (
                                        <option key={value} value={value}>{getDisplayLabel(value)}</option>
                                    ))}
                                </Select>
                            );
                        })()}
                    </FormControl>
                    {shouldAskTakingCourseInSingapore({
                        locationBasedGst: isSelectedCertLocationBasedGst(),
                        nationality: formData.nationality,
                    }) && (
                        <FormControl mb={{base: 4, md: 5}} isInvalid={touchedFields.has('taking_course_in_singapore') && !formData.taking_course_in_singapore}>
                            <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                Are you taking this course in Singapore?
                                <Text as="span" color="red.500" ml={1}>*</Text>
                            </FormLabel>
                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                                {[
                                    { value: 'yes', label: 'Yes' },
                                    { value: 'no', label: 'No' },
                                ].map((option) => {
                                    const isSelected = formData.taking_course_in_singapore === option.value;
                                    return (
                                        <Button
                                            key={option.value}
                                            onClick={() => {
                                                markFieldTouched('taking_course_in_singapore');
                                                handleChange('taking_course_in_singapore', option.value);
                                            }}
                                            colorScheme={isSelected ? 'blue' : 'gray'}
                                            variant={isSelected ? 'solid' : 'outline'}
                                            isDisabled={isReadOnly}
                                            size="md"
                                            py={{ base: 4, md: 5 }}
                                            px={4}
                                            whiteSpace="normal"
                                            height="auto"
                                            textAlign="left"
                                            fontWeight={isSelected ? 'bold' : 'medium'}
                                            borderColor={isSelected ? 'blue.500' : 'gray.300'}
                                        >
                                            {option.label}
                                        </Button>
                                    );
                                })}
                            </SimpleGrid>
                            {touchedFields.has('taking_course_in_singapore') && !formData.taking_course_in_singapore && (
                                <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                    Please select an option
                                </Text>
                            )}
                        </FormControl>
                    )}
                </React.Fragment>
            ),
            'nric': (
                <FormControl key="nric" mb={{base: 4, md: 5}}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        {getDynamicLabel('nric', 'NRIC Number')}
                        <Text as="span" color="red.500" ml={1}>*</Text>
                    </FormLabel>
                    <Input
                        value={formData.nric || ''}
                        onChange={(e) => {
                            const nric = isSingaporeCitizenOrPR(formData.nationality) ? e.target.value.toUpperCase() : e.target.value;
                            handleChange("nric", nric);
                        }}
                        isReadOnly={isReadOnly}
                        bg={isReadOnly ? "gray.50" : "white"}
                        onBlur={(e) => {
                            markFieldTouched('nric');
                            const nric = e.target.value;
                            const isValid = validateNRIC(nric, formData.nationality);
                            const isSG = isSingaporeCitizenOrPR(formData.nationality);
                            
                            setValidationErrors(prev => ({
                                ...prev,
                                nric: !isValid && nric !== "" 
                                    ? (isSG 
                                        ? "Invalid NRIC / NRIC format" 
                                        : (requiresPassport(formData.nationality)
                                            ? "Please enter your passport number"
                                            : "Please enter your FIN number"))
                                    : false
                            }));
                        }}
                        placeholder={(() => {
                            // Get placeholder from field_behaviors (dynamic or static)
                            const fieldConfig = basicFieldsOrder.find(f => f.field_key === 'nric');
                            if (fieldConfig) {
                                const placeholder = getFieldPlaceholder(fieldConfig);
                                if (placeholder) return placeholder;
                            }
                            // Fallback to hardcoded logic if no database placeholder
                            return requiresPassport(formData.nationality) ? "Enter passport number" :
                                   requiresFIN(formData.nationality) ? "F1234567X" :
                                   "S9912345Z";
                        })()}
                        size={{base: "lg", md: "md"}}
                        height={{base: "48px", md: "40px"}}
                        type="text"
                        maxLength={isSingaporeCitizenOrPR(formData.nationality) || requiresFIN(formData.nationality) ? 9 : undefined}
                        isInvalid={touchedFields.has('nric') && validationErrors.nric}
                    />
                    {touchedFields.has('nric') && validationErrors.nric && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            {validationErrors.nric}
                        </Text>
                    )}
                </FormControl>
            ),
            'dob': (
                <FormControl key="dob" mb={{base: 4, md: 5}}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        Date of Birth<Text as="span" color="red.500" ml={1}>*</Text>
                    </FormLabel>
                    <Input
                        value={formData.displayDob || ''}
                        onChange={(e) => {
                            const inputDate = e.target.value;
                            handleChange("displayDob", inputDate);

                            const date = new Date(inputDate);
                            if (!isNaN(date)) {
                                const storedFormat = date.toISOString().split('T')[0];
                                const age = calculateAge(storedFormat);
                                handleChange("dob", storedFormat);
                                handleChange("birth_age", age);

                                if (isUnderMinimumRegistrationAge(age)) {
                                    setValidationErrors(prev => ({...prev, dob: "under_age"}));
                                } else {
                                    setValidationErrors(prev => ({...prev, dob: false}));
                                }
                            }
                        }}
                        isReadOnly={isReadOnly}
                        bg={isReadOnly ? "gray.50" : "white"}
                        onBlur={(e) => {
                            markFieldTouched('dob');
                            const inputDate = e.target.value;
                            if (!inputDate) {
                                setValidationErrors(prev => ({
                                    ...prev,
                                    dob: "Date of birth is required"
                                }));
                                return;
                            }

                            const date = new Date(inputDate);
                            if (isNaN(date)) {
                                setValidationErrors(prev => ({...prev, dob: "Invalid date"}));
                                return;
                            }

                            const age = calculateAge(date);
                            if (age < 0 || age > 150) {
                                setValidationErrors(prev => ({...prev, dob: "Invalid date of birth"}));
                                return;
                            }

                            if (isUnderMinimumRegistrationAge(age)) {
                                setValidationErrors(prev => ({...prev, dob: "under_age"}));
                                return;
                            }

                            setValidationErrors(prev => ({...prev, dob: false}));
                        }}
                        placeholder={(() => {
                            // Get placeholder from field_behaviors
                            const fieldConfig = basicFieldsOrder.find(f => f.field_key === 'dob');
                            if (fieldConfig) {
                                const placeholder = getFieldPlaceholder(fieldConfig);
                                if (placeholder) return placeholder;
                            }
                            // Fallback
                            return "Select date";
                        })()}
                        size={{base: "lg", md: "md"}}
                        height={{base: "48px", md: "40px"}}
                        type="date"
                        isInvalid={(touchedFields.has('dob') && validationErrors.dob) || isUnderMinimumRegistrationAge(formData.birth_age)}
                    />
                    {isUnderMinimumRegistrationAge(formData.birth_age) && (
                        <AgeEligibilityAlert
                            courseDetails={getSelectedCourseDetails()}
                            birthAge={formData.birth_age}
                        />
                    )}
                    {touchedFields.has('dob') && validationErrors.dob && validationErrors.dob !== "under_age" && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            {validationErrors.dob}
                        </Text>
                    )}
                </FormControl>
            ),
            'participant_name': (
                <FormControl key="participant_name" mb={{base: 4, md: 5}}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        {getDynamicLabel('participant_name', 'Full Name as Per NRIC')}
                        <Text as="span" color="red.500" ml={1}>*</Text>
                    </FormLabel>
                    <Input
                        value={formData.participant_name || ''}
                        onChange={(e) => handleChange("participant_name", e.target.value)}
                        isReadOnly={isReadOnly}
                        bg={isReadOnly ? "gray.50" : "white"}
                        size={{base: "lg", md: "md"}}
                        height={{base: "48px", md: "40px"}}
                        type="text"
                        placeholder={(() => {
                            // Get placeholder from field_behaviors
                            const nameFieldConfig = basicFieldsOrder.find(f => f.field_key === 'participant_name');
                            if (nameFieldConfig) {
                                const placeholder = getFieldPlaceholder(nameFieldConfig);
                                if (placeholder) return placeholder;
                            }
                            // Fallback
                            return '';
                        })()}
                    />
                </FormControl>
            ),
            'participant_email': (
                <FormControl key="participant_email" mb={{base: 4, md: 5}} isInvalid={touchedFields.has('email') && (validationErrors.email || validationErrors.invalidDomain)}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        Participant Email<Text as="span" color="red.500" ml={1}>*</Text>
                        {emailVerified && (
                            <Badge ml={2} colorScheme="green" fontSize="xs">
                                ✓ Verified
                            </Badge>
                        )}
                    </FormLabel>
                    <Input
                        value={formData.participant_email || ''}
                        placeholder={(() => {
                            // Get placeholder from field_behaviors
                            const emailFieldConfig = basicFieldsOrder.find(f => f.field_key === 'participant_email');
                            if (emailFieldConfig) {
                                const placeholder = getFieldPlaceholder(emailFieldConfig);
                                if (placeholder) return placeholder;
                            }
                            // Fallback
                            return '';
                        })()}
                        onChange={(e) => {
                            const newEmail = e.target.value;
                            
                            // Prevent email changes if already verified (better UX)
                            if (emailVerified && isInitialSaveComplete) {
                                toast({
                                    title: "Email Already Verified",
                                    description: "Email cannot be changed after verification. Please contact support if you need to change your email.",
                                    status: "warning",
                                    duration: 5000,
                                    isClosable: true,
                                });
                                return;
                            }
                            
                            handleChange("participant_email", newEmail);
                            
                            // If email changed and we have a registration ID, invalidate verification
                            if (registrationId && originalEmail && newEmail !== originalEmail) {
                                setEmailVerified(false);
                                setEmailVerificationSent(false);
                            }
                        }}
                        onBlur={async (e) => {
                            const newEmail = e.target.value;
                            
                            // If email changed and registration is saved, send new verification
                            if (registrationId && originalEmail && newEmail !== originalEmail && newEmail) {
                                // Update original email
                                setOriginalEmail(newEmail);
                                
                                // IMPORTANT: Update email in database immediately
                                // This ensures verification works correctly
                                const { error: updateError } = await supabase
                                    .from('registration')
                                    .update({ 
                                        participant_email: newEmail, // Update email in database
                                        email_verified: false, 
                                        email_verification_token: null,
                                        email_verification_token_expires_at: null
                                    })
                                    .eq('id', registrationId);
                                
                                if (updateError) {
                                    console.error('Error updating email:', updateError);
                                    toast({
                                        title: "Error Updating Email",
                                        description: "Failed to update email. Please try again.",
                                        status: "error",
                                        duration: 5000,
                                        isClosable: true,
                                    });
                                    return;
                                }
                                
                                // Send new verification email
                                await sendVerificationEmail(
                                    registrationId,
                                    newEmail,
                                    formData.participant_name
                                );
                            }
                            
                            // Mark as touched and validate
                            markFieldTouched('email');
                            const email = e.target.value;
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            const hasInvalidDomain = /\.\w$/.test(email);

                            setValidationErrors(prev => ({
                                ...prev,
                                email: !emailRegex.test(email) && email !== "",
                                invalidDomain: hasInvalidDomain
                            }));
                        }}
                        isReadOnly={isReadOnly}
                        bg={isReadOnly ? "gray.50" : "white"}
                        size={{base: "lg", md: "md"}}
                        height={{base: "48px", md: "40px"}}
                        type="email"
                        isInvalid={touchedFields.has('email') && (validationErrors.email || validationErrors.invalidDomain)}
                    />
                    {touchedFields.has('email') && validationErrors.email && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            Please enter a valid email address
                        </Text>
                    )}
                    {touchedFields.has('email') && validationErrors.invalidDomain && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            Invalid domain - domain must be at least 2 characters
                        </Text>
                    )}
                </FormControl>
            ),
            'mobile_no': (
                <FormControl key="mobile_no" mb={{base: 4, md: 5}} isInvalid={touchedFields.has('mobile') && validationErrors.mobile}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                        Mobile Number<Text as="span" color="red.500" ml={1}>*</Text>
                    </FormLabel>
                    <PhoneInput
                        international
                        defaultCountry="SG"
                        disabled={isReadOnly}
                        value={formData.mobile_no || ''}
                        onChange={(value) => {
                            handleChange("mobile_no", value || '');
                            // Only validate if field has been touched
                            if (touchedFields.has('mobile')) {
                                if (value) {
                                    try {
                                        const isValid = isValidPhone(value);
                                        setValidationErrors(prev => ({
                                            ...prev,
                                            mobile: !isValid
                                        }));
                                        if (isValid) {
                                            const phoneNumber = parsePhone(value);
                                            const formattedNumber = phoneNumber.format('INTERNATIONAL');
                                            const numberOnly = phoneNumber.nationalNumber;
                                            setFormData(prevData => ({
                                                ...prevData,
                                                mobile_no: formattedNumber,
                                                mobile_no_raw: numberOnly
                                            }));
                                        }
                                    } catch (error) {
                                        setValidationErrors(prev => ({...prev, mobile: true}));
                                    }
                                } else {
                                    setValidationErrors(prev => ({...prev, mobile: false}));
                                }
                            }
                        }}
                        onBlur={() => {
                            markFieldTouched('mobile');
                            // Validate on blur
                            const value = formData.mobile_no;
                            if (value) {
                                try {
                                    const isValid = isValidPhone(value);
                                    setValidationErrors(prev => ({...prev, mobile: !isValid}));
                                } catch (error) {
                                    setValidationErrors(prev => ({...prev, mobile: true}));
                                }
                            }
                        }}
                        inputComponent={Input}
                        style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        className="chakra-phone-input"
                        inputProps={{
                            isReadOnly: isReadOnly,
                            bg: isReadOnly ? "gray.50" : "white",
                            size: {base: "lg", md: "md"},
                            height: {base: "48px", md: "40px"},
                            isInvalid: touchedFields.has('mobile') && validationErrors.mobile
                        }}
                    />
                    {touchedFields.has('mobile') && validationErrors.mobile && (
                        <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                            Please enter a valid mobile number
                        </Text>
                    )}
                </FormControl>
            )
        };

        return fieldMap[fieldKey] || null;
    };

    // Render compulsory custom field (custom fields in Compulsory fields section) for Step 1
    const renderCompulsoryCustomField = (field) => {
        const fieldValue = getFieldValue(formData, field.field_key);
        // Only show error if field has been touched
        const isTouched = touchedFields.has(field.field_key);
        const fieldError = isTouched ? validationErrors[field.field_key] : null;
        const isRequired = getEffectiveRequiredStatus(field);
        
        // Get dynamic label
        const fieldLabel = getFieldDynamicLabel(field);
        
        // Get custom placeholder
        const placeholder = getFieldPlaceholder(field);

        // Validate field helper
        const validateField = (field, value) => {
            const isRequired = getEffectiveRequiredStatus(field);
            if (isRequired && (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
                return `${field.field_label} is required`;
            }
            if (field.field_type === 'email' && value) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    return 'Please enter a valid email address';
                }
            }
            if (field.field_type === 'phone' && value) {
                const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
                if (!phoneRegex.test(value.replace(/\s/g, ''))) {
                    return 'Please enter a valid phone number';
                }
            }
            return false;
        };

        const commonProps = {
            value: fieldValue || '',
            onChange: (e) => handleChange(field.field_key, e.target.value),
            onBlur: () => {
                // Mark field as touched on blur
                markFieldTouched(field.field_key);
                const error = validateField(field, fieldValue);
                setValidationErrors(prev => ({
                    ...prev,
                    [field.field_key]: error
                }));
            },
            isInvalid: !!fieldError,
            bg: isReadOnly ? "gray.50" : "white",
            isReadOnly: isReadOnly,
            isDisabled: isReadOnly,
            size: {base: "lg", md: "md"},
            height: {base: "48px", md: "40px"},
            placeholder: placeholder
        };

        switch (field.field_type) {
            case 'text':
            case 'email':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Input
                            {...commonProps}
                            type={field.field_type === 'email' ? 'email' : 'text'}
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'phone':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <PhoneInput
                            international
                            defaultCountry="SG"
                            disabled={isReadOnly}
                            value={fieldValue || ''}
                            onChange={(value) => handleChange(field.field_key, value || '')}
                            inputComponent={Input}
                            inputProps={{
                                ...commonProps,
                                isInvalid: !!fieldError
                            }}
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'textarea':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Textarea
                            {...commonProps}
                            rows={3}
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'date':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Input
                            {...commonProps}
                            type="date"
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'number':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Input
                            {...commonProps}
                            type="number"
                            onChange={(e) => handleChange(field.field_key, parseFloat(e.target.value) || 0)}
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'select':
                const options = field.field_options || [];
                const isOtherOptionSelected = fieldValue && !options.includes(fieldValue) && fieldValue !== '';

                // For small number of options, show as buttons for better UX on both web and mobile
                if (options.length > 0 && options.length < 5) {
                    return (
                        <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                            <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                {fieldLabel}
                                {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                            </FormLabel>
                            <VStack align="stretch" spacing={2}>
                                <SimpleGrid 
                                    columns={{ 
                                        base: 1, 
                                        sm: options.length === 2 ? 2 : (options.length === 3 ? 3 : 2), 
                                        md: Math.min(options.length, 4) 
                                    }} 
                                    spacing={2}
                                >
                                    {options.map((option) => {
                                        const isOthers = option === 'Others' || option === 'Other';
                                        const isSelected = isOthers ? isOtherOptionSelected : fieldValue === option;
                                        
                                        return (
                                            <Button
                                                key={option}
                                                onClick={() => {
                                                    if (isOthers) {
                                                        handleChange(field.field_key, '');
                                                    } else {
                                                        handleChange(field.field_key, option);
                                                    }
                                                }}
                                                colorScheme={isSelected ? "blue" : "gray"}
                                                variant={isSelected ? "solid" : "outline"}
                                                isDisabled={isReadOnly}
                                                size="md"
                                                py={{ base: 4, md: 5 }}
                                                px={4}
                                                whiteSpace="normal"
                                                height="auto"
                                                textAlign="center"
                                                fontWeight={isSelected ? "bold" : "medium"}
                                                borderColor={isSelected ? "blue.500" : "gray.300"}
                                                _hover={!isReadOnly ? {
                                                    bg: isSelected ? "blue.600" : "gray.100",
                                                    borderColor: "blue.500"
                                                } : {}}
                                            >
                                                {option}
                                            </Button>
                                        );
                                    })}
                                </SimpleGrid>
                                {isOtherOptionSelected && !isReadOnly && (
                                    <Input
                                        placeholder={placeholder || `Please specify ${fieldLabel.toLowerCase()}...`}
                                        value={fieldValue === 'Others' || fieldValue === 'Other' ? '' : fieldValue}
                                        onChange={(e) => handleChange(field.field_key, e.target.value)}
                                        size="md"
                                        mt={1}
                                        autoFocus
                                        borderColor="blue.300"
                                        focusBorderColor="blue.500"
                                    />
                                )}
                            </VStack>
                            {fieldError && (
                                <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                    {fieldError}
                                </Text>
                            )}
                        </FormControl>
                    );
                }

                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Select
                            {...commonProps}
                            placeholder={`Select ${fieldLabel}`}
                        >
                            {(field.field_options || []).map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </Select>
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );

            case 'boolean':
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}}>
                        <HStack>
                            <Checkbox
                                isChecked={!!fieldValue}
                                onChange={(e) => handleChange(field.field_key, e.target.checked)}
                                isDisabled={isReadOnly}
                            >
                                <FormLabel mb={0} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                    {fieldLabel}
                                    {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                                </FormLabel>
                            </Checkbox>
                        </HStack>
                    </FormControl>
                );

            case 'file':
            case 'file_multiple':
                const selectedFilesForField = selectedFiles[field.field_key];
                const isUploading = uploadingFiles.has(field.field_key);
                const hasSelectedFiles = selectedFilesForField && selectedFilesForField.length > 0;
                const hasUploadedFiles = fieldValue && (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== '');
                
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Box>
                            <input
                                type="file"
                                multiple={field.field_type === 'file_multiple'}
                                onChange={(e) => handleFileSelection(field.field_key, e.target.files)}
                                style={{ display: 'none' }}
                                id={`compulsory-file-input-${field.field_key}`}
                                accept="image/*,.pdf,.doc,.docx,.txt"
                                disabled={isReadOnly}
                            />
                            
                            {!isReadOnly && (
                                <Box textAlign="left" mb={hasUploadedFiles ? 4 : 0}>
                                    <Button
                                        onClick={() => {
                                            const input = document.getElementById(`compulsory-file-input-${field.field_key}`);
                                            if (input) input.click();
                                        }}
                                        disabled={isUploading}
                                        colorScheme="blue"
                                        variant={hasUploadedFiles ? "solid" : "outline"}
                                        size="sm"
                                        leftIcon={<Text fontSize="lg">+</Text>}
                                    >
                                        {hasUploadedFiles ? 
                                            (field.field_type === 'file_multiple' ? 'Add More' : 'Change File') 
                                            : 'Select File'}
                                    </Button>
                                </Box>
                            )}
                            
                            {selectedFilesForField && selectedFilesForField.length > 0 && (
                                <Box mt={4} p={3} border="1px dashed" borderColor="blue.300" borderRadius="md" bg="blue.50">
                                    <Text fontSize="xs" fontWeight="bold" mb={2} color="blue.700" textTransform="uppercase">
                                        Staged for upload:
                                    </Text>
                                    <VStack spacing={2} align="stretch">
                                        {Array.from(selectedFilesForField).map((file, index) => (
                                            <FileAttachmentRow
                                                key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                                                file={file}
                                                fileName={file.name}
                                                variant="staged"
                                                onRemove={() => {
                                                    const newFiles = Array.from(selectedFilesForField).filter((_, i) => i !== index);
                                                    const dataTransfer = new DataTransfer();
                                                    newFiles.forEach(f => dataTransfer.items.add(f));
                                                    setSelectedFiles(prev => ({ ...prev, [field.field_key]: dataTransfer.files }));
                                                }}
                                            />
                                        ))}
                                    </VStack>
                                    <Text fontSize="10px" color="blue.600" mt={2} fontStyle="italic">
                                        Files will be uploaded automatically when you save.
                                    </Text>
                                </Box>
                            )}
                            
                            {fieldValue && (
                                <Box mt={4}>
                                    <Text fontSize="xs" fontWeight="bold" mb={2} color="green.700" textTransform="uppercase">
                                        ✓ Uploaded
                                    </Text>
                                    <VStack spacing={2} align="stretch">
                                        {Array.isArray(fieldValue) ? (
                                            fieldValue.map((filePath, index) => {
                                                const { data: urlData } = supabase.storage
                                                    .from('Registration_data')
                                                    .getPublicUrl(filePath);
                                                return (
                                                    <FileAttachmentRow
                                                        key={`${filePath}-${index}`}
                                                        fileName={filePath.split('/').pop()}
                                                        previewSrc={urlData?.publicUrl}
                                                        variant="uploaded"
                                                        canRemove={!isReadOnly}
                                                        onRemove={() => {
                                                            const newValue = fieldValue.filter((_, i) => i !== index);
                                                            handleChange(field.field_key, newValue);
                                                        }}
                                                    />
                                                );
                                            })
                                        ) : (
                                            (() => {
                                                const { data: urlData } = supabase.storage
                                                    .from('Registration_data')
                                                    .getPublicUrl(fieldValue);
                                                return (
                                                    <FileAttachmentRow
                                                        fileName={fieldValue.split('/').pop()}
                                                        previewSrc={urlData?.publicUrl}
                                                        variant="uploaded"
                                                        canRemove={!isReadOnly}
                                                        onRemove={() => handleChange(field.field_key, '')}
                                                    />
                                                );
                                            })()
                                        )}
                                    </VStack>
                                </Box>
                            )}
                        </Box>
                    </FormControl>
                );

            default:
                return (
                    <FormControl key={field.id} mb={{base: 4, md: 5}} isInvalid={!!fieldError}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                            {fieldLabel}
                            {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                        </FormLabel>
                        <Input
                            {...commonProps}
                            type="text"
                        />
                        {fieldError && (
                            <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                {fieldError}
                            </Text>
                        )}
                    </FormControl>
                );
        }
    };

    // Helper function to get field value from formData
    const getFieldValue = (formData, fieldKey) => {
        // First check if it's a direct key
        if (formData[fieldKey] !== undefined && formData[fieldKey] !== null && formData[fieldKey] !== '') {
            return formData[fieldKey];
        }
        
        // Then check for nested keys (e.g., "personal_details.gender")
        if (fieldKey.includes('.')) {
            const keys = fieldKey.split('.');
            let current = formData;
            for (const key of keys) {
                if (current && typeof current === 'object' && key in current) {
                    current = current[key];
                } else {
                    return '';
                }
            }
            return current || '';
        }
        
        return '';
    };

    // Helper function to check if a field is conditionally required
    const isFieldConditionallyRequired = (field) => {
        if (!field.conditional_requirements) {
            return false;
        }

        const condition = field.conditional_requirements.required_when;
        if (!condition) {
            return false;
        }

        const triggerField = condition.field;
        const triggerValue = getFieldValue(formData, triggerField);
        const operator = condition.operator;
        const expectedValue = condition.value;

        switch (operator) {
            case 'equals':
                return triggerValue === expectedValue;
            case 'contains':
                return Array.isArray(triggerValue) && triggerValue.includes(expectedValue);
            case 'in':
                return Array.isArray(expectedValue) && expectedValue.includes(triggerValue);
            case 'not_equals':
                return triggerValue !== expectedValue;
            default:
                return false;
        }
    };

    // Get effective requirement status (static or conditional)
    const getEffectiveRequiredStatus = (field) => {
        if (field.conditional_requirements) {
            return isFieldConditionallyRequired(field);
        }
        return field.is_required;
    };

    // Check if required additional fields are filled
    const checkRequiredAdditionalFields = () => {
        const requiredFields = additionalFields.filter(field => {
            const isRequired = getEffectiveRequiredStatus(field);
            return isRequired && 
                field.field_key !== 'legal.declaration_date' && // Exclude auto-filled declaration date
                !field.field_key.includes('signature'); // Exclude all signature fields
        });
        
        for (const field of requiredFields) {
            const fieldValue = getFieldValue(formData, field.field_key);
            const stagedFiles = selectedFiles[field.field_key];
            
            // A field is valid if it has a value in DB/formData OR it has staged files ready to be uploaded
            const hasValue = (fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0));
            const hasStagedFiles = (stagedFiles && stagedFiles.length > 0);
            
            if (!hasValue && !hasStagedFiles) {
                return false;
            }
        }
        return true;
    };

    // Helper functions for progress calculation and visibility
    const shouldShowField = (field) => {
        if (!field.conditional_requirements || !field.conditional_requirements.show_when) return true;
        const { field: triggerField, value: triggerValue, operator } = field.conditional_requirements.show_when;
        const currentVal = getFieldValue(formData, triggerField);
        if (operator === 'equals') return currentVal === triggerValue;
        if (operator === 'not_equals') return currentVal !== triggerValue;
        if (operator === 'contains') return Array.isArray(currentVal) && currentVal.includes(triggerValue);
        return true;
    };

    // Calculate compulsory fields completion for progress bar
    // Includes basic fields + all required additional fields
    const getCompulsoryFieldsProgress = () => {
        const compulsoryFields = new Map();
        
        // 1. Course selection as first compulsory item
        compulsoryFields.set('course', !!formData.draft_cert_run_code);
        
        // 2. Basic required fields (core system fields)
        const basicRequiredFieldKeys = ['nationality', 'nric', 'dob', 'participant_name', 'participant_email', 'mobile_no'];
        basicRequiredFieldKeys.forEach(key => {
            const val = formData[key];
            compulsoryFields.set(key, !!(val && val.toString().trim() !== ''));
        });

        // Location-based GST self-declaration (e-learning / overseas)
        if (shouldAskTakingCourseInSingapore({
            locationBasedGst: isSelectedCertLocationBasedGst(),
            nationality: formData.nationality,
        })) {
            compulsoryFields.set(
                'taking_course_in_singapore',
                formData.taking_course_in_singapore === 'yes' || formData.taking_course_in_singapore === 'no'
            );
        }
        
        // 3. Add all required additional fields from database
        // Use Map to ensure each field_key is only counted once
        if (additionalFields && additionalFields.length > 0) {
            additionalFields.forEach(field => {
                // Skip if already in basicRequiredFieldKeys to avoid double counting
                if (basicRequiredFieldKeys.includes(field.field_key)) return;
                
                // Exclude auto-filled and signature fields from progress count
                if (field.field_key === 'legal.declaration_date' || 
                    field.field_key.includes('signature') || 
                    field.field_key === 'terms') return;

                const isRequired = getEffectiveRequiredStatus(field);
                const isVisible = shouldShowField(field);
                
                if (isRequired && isVisible) {
                    const fieldValue = getFieldValue(formData, field.field_key);
                    const stagedFiles = selectedFiles[field.field_key];
                    const isFilled = (
                        (fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0)) ||
                        (stagedFiles && stagedFiles.length > 0)
                    );
                    compulsoryFields.set(field.field_key, !!isFilled);
                }
            });
        }
        
        // Base fields required for Save & Continue
        const allBaseFilled = Array.from(compulsoryFields.values()).every(v => v);
        
        // 4. Final steps required for Submit Registration
        const finalFields = new Map();
        // Email verification is a final requirement after initial save
        if (isInitialSaveComplete) {
            finalFields.set('email_verified', emailVerified);
        }
        finalFields.set('terms', hasAgreedToTerms);
        finalFields.set('signature', !isSignatureEmpty);
        
        const allFinalFilled = Array.from(finalFields.values()).every(v => v);
        
        const totalFilledCount = Array.from(compulsoryFields.values()).filter(v => v).length + 
                                 Array.from(finalFields.values()).filter(v => v).length;
        const totalCount = compulsoryFields.size + finalFields.size;
        
        return { 
            filledCount: totalFilledCount, 
            totalCount, 
            canSave: allBaseFilled,
            canSubmit: allBaseFilled && allFinalFilled,
            allFilled: allBaseFilled && allFinalFilled // for backwards compat in UI
        };
    };
    
    const progressData = getCompulsoryFieldsProgress();

    // Send verification email
    const sendVerificationEmail = async (registrationId, participantEmail, participantName) => {
        if (!registrationId || !participantEmail) return false;

        setIsSendingVerification(true);
        try {
            const { data, error } = await supabase.functions.invoke('send-verification-email', {
                body: {
                    registrationId,
                    participantEmail,
                    participantName: participantName || formData.participant_name || 'Participant',
                },
            });

            if (error) throw error;

            if (data.success) {
                setEmailVerificationSent(true);
                setEmailVerified(false); // Reset to false when new email is sent
                return true; // Return success
            } else {
                throw new Error(data.error || 'Failed to send verification email');
            }
        } catch (error) {
            console.error('Error sending verification email:', error);
            // Only show error toast if not called from initial save (to avoid multiple toasts)
            // Error will be handled by caller
            return false;
        } finally {
            setIsSendingVerification(false);
        }
    };

    // File upload handlers for compulsory fields
    const handleFileSelection = (fieldKey, files) => {
        if (!files || files.length === 0) return;
        setSelectedFiles(prev => ({
            ...prev,
            [fieldKey]: files
        }));
    };

    const handleCompulsoryFileUpload = async (field) => {
        const files = selectedFiles[field.field_key];
        if (!files || files.length === 0) {
            toast({
                title: "No files selected",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Auto-save if needed
        if (!registrationId && formData.participant_name && formData.participant_email && formData.draft_cert_run_code) {
            try {
                const registrationData = {
                    participant_name: formData.participant_name,
                    participant_email: formData.participant_email,
                    nationality: formData.nationality || '',
                    nric: formData.nric || '',
                    dob: formData.dob || '',
                    birth_age: formData.birth_age || '',
                    mobile_no: formData.mobile_no || '',
                    registration_stage: "draft",
                    draft_cert_run_code: formData.draft_cert_run_code,
                };
                
                const { data, error } = await supabase
                    .from('registration')
                    .insert([registrationData])
                    .select("id")
                    .single();
                
                if (error) throw error;
                
                const savedId = data.id;
                setRegistrationId(savedId);
                setIsInitialSaveComplete(true);
                
                // Send verification email
                if (formData.participant_email) {
                    await sendVerificationEmail(savedId, formData.participant_email, formData.participant_name);
                }
            } catch (error) {
                console.error('Auto-save error:', error);
                toast({
                    title: "Registration Required",
                    description: "Please fill in your basic information (name, email, etc.) first to enable file uploads.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }
        } else if (!registrationId) {
            toast({
                title: "Registration Required",
                description: "Please fill in your basic information (name, email, etc.) first to enable file uploads.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        if (uploadingFiles.has(field.field_key)) {
            toast({
                title: "Please wait for the current upload to complete.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            setUploadingFiles(prev => new Set(prev).add(field.field_key));
            
            const studentName = formData.participant_name || 'Unknown';
            const sanitizedName = studentName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
            
            const urlParams = new URLSearchParams(window.location.search);
            const isFullCertification = (formData.full_certification_id || urlParams.get('full_certification')) ? true : false;
            const fullCertificationId = formData.full_certification_id || urlParams.get('full_certification');
            const certificationRunId = formData.certification_run_id || urlParams.get('certification_run_id');
            
            const uploadedFiles = [];
            const existingValue = getFieldValue(formData, field.field_key);
            const existingFilesCount = Array.isArray(existingValue) ? existingValue.length : (existingValue ? 1 : 0);
            
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileExtension = file.name.split('.').pop() || '';
                const timestamp = new Date().toISOString().split('T')[0];
                const fieldIdentifier = field.field_key.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                const fileName = `${sanitizedName}_${fieldIdentifier}_${timestamp}_${existingFilesCount + i + 1}.${fileExtension}`;
                
                let folderPath;
                if (isFullCertification) {
                    if (!fullCertificationId || !certificationRunId) {
                        toast({
                            title: "Select certification run",
                            description: "Please select a certification run before uploading files.",
                            status: "warning",
                            duration: 4000,
                            isClosable: true,
                        });
                        break;
                    }
                    folderPath = `registrations/full__cert/${fullCertificationId}/${certificationRunId}/${sanitizedName}`;
                } else if (registrationId) {
                    folderPath = `registrations/${registrationId}`;
                } else {
                    toast({
                        title: "Registration Required",
                        description: "Please fill in your basic information first to enable file uploads.",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                    });
                    break;
                }
                
                const filePath = `${folderPath}/${fileName}`;
                
                const { error } = await supabase.storage
                    .from('Registration_data')
                    .upload(filePath, file, {
                        cacheControl: '3600',
                        upsert: true
                    });

                if (error) {
                    console.error('Upload error:', error);
                    toast({
                        title: "Upload Failed",
                        description: `Failed to upload ${file.name}: ${error.message}`,
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                    continue;
                }

                uploadedFiles.push(filePath);
            }

            if (uploadedFiles.length > 0) {
                const currentValue = getFieldValue(formData, field.field_key);
                const newValue = field.field_type === 'file_multiple' 
                    ? [...(Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : []), ...uploadedFiles]
                    : uploadedFiles[0];
                
                handleChange(field.field_key, newValue);
                
                setSelectedFiles(prev => {
                    const newState = { ...prev };
                    delete newState[field.field_key];
                    return newState;
                });
                
                toast({
                    title: "Upload Successful",
                    description: `Successfully uploaded ${uploadedFiles.length} file(s)`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast({
                title: "Upload Failed",
                description: error.message || "An error occurred during upload",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(field.field_key);
                return newSet;
            });
        }
    };

    // Resend verification email
    const resendVerificationEmail = async () => {
        if (!registrationId || !formData.participant_email) {
            toast({
                title: "Cannot Resend",
                description: "Please save your registration first.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const result = await sendVerificationEmail(
            registrationId,
            formData.participant_email,
            formData.participant_name
        );

        if (result) {
            toast({
                title: "Verification Email Resent",
                description: `A new verification email has been sent to ${formData.participant_email}. Please check your inbox.`,
                status: "info",
                duration: 4000,
                isClosable: true,
            });
        } else {
            toast({
                title: "Could Not Resend Email",
                description: "We couldn't send the verification email. Please try again in a moment, or contact support if this keeps happening.",
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        }
    };

    // Check verification status periodically
    useEffect(() => {
        if (!registrationId || emailVerified) return;

        const checkVerificationStatus = async () => {
            try {
                const { data, error } = await supabase
                    .from('registration')
                    .select('email_verified')
                    .eq('id', registrationId)
                    .single();

                if (!error && data && data.email_verified) {
                    setEmailVerified(true);
                    toast({
                        title: "Email Verified!",
                        description: "Your email has been verified. You can now submit your registration.",
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error('Error checking verification status:', error);
            }
        };

        // Check every 5 seconds if not verified
        const interval = setInterval(checkVerificationStatus, 5000);
        return () => clearInterval(interval);
    }, [registrationId, emailVerified]);

    // Helper to upload all staged files automatically and return the new paths
    const uploadAllStagedFiles = async (targetId) => {
        const fieldKeys = Object.keys(selectedFiles);
        if (fieldKeys.length === 0) return {};

        const newPaths = {};
        
        for (const fieldKey of fieldKeys) {
            const files = selectedFiles[fieldKey];
            if (!files || files.length === 0) continue;

            const field = additionalFields.find(f => f.field_key === fieldKey) || 
                          compulsoryCustomFields.find(f => f.field_key === fieldKey);
            
            if (field) {
                try {
                    // Temporarily set uploading state for this field
                    setUploadingFiles(prev => new Set(prev).add(fieldKey));
                    
                    const studentName = formData.participant_name || 'Unknown';
                    const sanitizedName = studentName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                    
                    const urlParams = new URLSearchParams(window.location.search);
                    const isFullCert = (formData.full_certification_id || urlParams.get('full_certification')) ? true : false;
                    const fCertId = formData.full_certification_id || urlParams.get('full_certification');
                    const cRunId = formData.certification_run_id || urlParams.get('certification_run_id');
                    
                    const uploadedFiles = [];
                    const existingValue = getFieldValue(formData, fieldKey);
                    const existingFilesCount = Array.isArray(existingValue) ? existingValue.length : (existingValue ? 1 : 0);
                    
                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const fileExtension = file.name.split('.').pop() || '';
                        const timestamp = new Date().toISOString().split('T')[0];
                        const fieldIdentifier = fieldKey.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                        const fileName = `${sanitizedName}_${fieldIdentifier}_${timestamp}_${existingFilesCount + i + 1}.${fileExtension}`;
                        
                        let folderPath;
                        if (isFullCert && fCertId && cRunId) {
                            folderPath = `registrations/full__cert/${fCertId}/${cRunId}/${sanitizedName}`;
                        } else {
                            folderPath = `registrations/${targetId}`;
                        }
                        
                        const filePath = `${folderPath}/${fileName}`;
                        const { error } = await supabase.storage
                            .from('Registration_data')
                            .upload(filePath, file, { cacheControl: '3600', upsert: true });

                        if (error) throw error;
                        uploadedFiles.push(filePath);
                    }

                    if (uploadedFiles.length > 0) {
                        const currentValue = getFieldValue(formData, fieldKey);
                        const newValue = field.field_type === 'file_multiple' 
                            ? [...(Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : []), ...uploadedFiles]
                            : uploadedFiles[0];
                        
                        newPaths[fieldKey] = newValue;
                        
                        // Update local state with proper nesting
                        setFormData(prev => {
                            const newData = { ...prev };
                            if (fieldKey.includes('.')) {
                                const keys = fieldKey.split('.');
                                let current = newData;
                                for (let i = 0; i < keys.length - 1; i++) {
                                    if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                                        current[keys[i]] = {};
                                    }
                                    current = current[keys[i]];
                                }
                                current[keys[keys.length - 1]] = newValue;
                            } else {
                                newData[fieldKey] = newValue;
                            }
                            return newData;
                        });

                        // Clear selected files for this field
                        setSelectedFiles(prev => {
                            const newState = { ...prev };
                            delete newState[fieldKey];
                            return newState;
                        });
                    }
                } catch (error) {
                    console.error(`Upload error for ${fieldKey}:`, error);
                    toast({
                        title: "Upload Failed",
                        description: `Failed to upload files for ${field.field_label}. Please try again manually.`,
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                } finally {
                    setUploadingFiles(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(fieldKey);
                        return newSet;
                    });
                }
            }
        }
        return newPaths;
    };

    // Helper to merge dotted keys into a nested object
    const mergeDottedPaths = (target, dottedPaths) => {
        const result = { ...target };
        Object.entries(dottedPaths).forEach(([key, value]) => {
            if (key.includes('.')) {
                const keys = key.split('.');
                let current = result;
                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                        current[keys[i]] = {};
                    }
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = value;
            } else {
                result[key] = value;
            }
        });
        return result;
    };

    const handleSaveAndContinue = async () => {
        setIsSaving(true);
        try {
            devLog('Saving initial registration...');
            devLog('Form data:', formData);

            const params = new URLSearchParams(location.search);
            // const fullCertification = params.get('full_certification');

            if (!formData.draft_cert_run_code) {
                throw new Error('No certification run selected');
            }

            // Get the selected certification run details
            const selectedCertRun = courseRuns.find(c => c.id === formData.draft_cert_run_code);
            
            if (!selectedCertRun) {
                throw new Error('Selected certification run not found');
            }

            // Prepare additional data with proper nesting
            let additionalData = {};
            
            // Get all additional fields to build the correct structure
            const { data: fieldsList } = await supabase
                .from('additional_registration_fields')
                .select('field_key')
                .or('field_status.is.null,field_status.eq.active')
                .eq('is_original_field', false);

            if (fieldsList) {
                fieldsList.forEach(field => {
                    const fieldValue = getFieldValue(formData, field.field_key);
                    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
                        const keys = field.field_key.split('.');
                        if (keys.length > 1) {
                            let current = additionalData;
                            for (let i = 0; i < keys.length - 1; i++) {
                                if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                                    current[keys[i]] = {};
                                }
                                current = current[keys[i]];
                            }
                            current[keys[keys.length - 1]] = fieldValue;
                        } else {
                            additionalData[field.field_key] = fieldValue;
                        }
                    }
                });
            }

            if (formData.taking_course_in_singapore === 'yes' || formData.taking_course_in_singapore === 'no') {
                additionalData.taking_course_in_singapore = formData.taking_course_in_singapore;
            } else {
                delete additionalData.taking_course_in_singapore;
            }

            // Create a single registration record for the certification
            const registrationData = {
                participant_name: formData.participant_name,
                participant_email: formData.participant_email,
                nationality: formData.nationality,
                nric: formData.nric,
                dob: formData.dob,
                birth_age: formData.birth_age,
                mobile_no: formData.mobile_no,
                registration_stage: "draft",
                draft_cert_run_code: formData.draft_cert_run_code,
                additional_data: Object.keys(additionalData).length > 0 ? additionalData : null
            };

            devLog('Attempting to save registration with data:', registrationData);

            let data, error;
            
            if (registrationId) {
                // Update existing registration
                const result = await supabase
                    .from('registration')
                    .update(registrationData)
                    .eq('id', registrationId)
                    .select("id")
                    .single();
                
                data = result.data;
                error = result.error;
            } else {
                // Create new registration
                const result = await supabase
                    .from('registration')
                    .insert([registrationData])
                    .select("id")
                    .single();
                
                data = result.data;
                error = result.error;
            }

            devLog('Save result:', {data, error});

            if (error) {
                console.error('Registration save error:', error);
                setIsSaving(false);
                toast({
                    title: "Save Failed",
                    description: `Failed to save registration. Error: ${error.message}. Please try again.`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                const savedId = data.id;
                setRegistrationId(savedId);
                
                // Automatic Upload: If there are staged files, upload them now
                const newFilePaths = await uploadAllStagedFiles(savedId);
                
                // If files were uploaded, perform a SECOND update to save the paths in additional_data
                if (Object.keys(newFilePaths).length > 0) {
                    devLog('Persisting uploaded file paths:', newFilePaths);
                    
                    // Merge new paths into additionalData using our helper
                    const finalAdditionalData = mergeDottedPaths(registrationData.additional_data || {}, newFilePaths);
                    
                    const { error: pathUpdateError } = await supabase
                        .from('registration')
                        .update({ additional_data: finalAdditionalData })
                        .eq('id', savedId);
                        
                    if (pathUpdateError) {
                        console.error('Error persisting file paths:', pathUpdateError);
                    } else {
                        devLog('File paths persisted successfully');
                    }
                }
                
                setIsInitialSaveComplete(true);
                setIsSaving(false);

                // Check if email was changed - if so, invalidate verification
                if (originalEmail && formData.participant_email !== originalEmail) {
                    setEmailVerified(false);
                    // Update email_verified to false in database
                    await supabase
                        .from('registration')
                        .update({ email_verified: false, email_verification_token: null })
                        .eq('id', savedId);
                }

                // Send verification email if we have an email address (silently, no toast yet)
                if (formData.participant_email) {
                    await sendVerificationEmail(
                        savedId,
                        formData.participant_email,
                        formData.participant_name
                    );
                }

                setOriginalEmail(formData.participant_email || '');

                // Combined toast notification (instead of multiple)
                toast({
                    title: "Registration Saved",
                    description: formData.participant_email 
                        ? "Your registration has been saved. A verification email has been sent to your inbox."
                        : "Your registration has been saved. You can now continue.",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });

                // Update URL to include registration ID without page reload/scroll
                // Using history.replaceState to avoid React Router's scroll restoration
                window.history.replaceState(
                    { preventScroll: true },
                    '',
                    fullCertRegistrationPath(savedId)
                );
                
                // REMOVED: Jarring scroll-into-view behavior
                // Let users continue from where they are - the toast notification is sufficient feedback
            }
        } catch (error) {
            console.error('Unexpected error during save:', error);
            setIsSaving(false);
            toast({
                title: "Save Failed",
                description: `Registration save failed: ${error.message}. Please try again.`,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            devLog('Starting registration process...');
            devLog('Form data:', formData);

            if (!registrationId) {
                throw new Error('No registration ID found. Please save your registration first.');
            }

            // Automatic Upload: If there are any last-minute staged files, upload them now
            const newFilePaths = await uploadAllStagedFiles(registrationId);

            // Fetch current registration to check for rejection_reason and preserve rejection_history
            const { data: currentRegistration, error: fetchError } = await supabase
                .from('registration')
                .select('rejection_reason, additional_data')
                .eq('id', registrationId)
                .single();

            if (fetchError) {
                console.error('Error fetching current registration:', fetchError);
                // Continue anyway, but log the error
            }

            const hasRejectionReason = currentRegistration?.rejection_reason && 
                                      currentRegistration.rejection_reason.trim() !== '';
            const existingAdditionalData = currentRegistration?.additional_data || {};
            
            // Preserve rejection_history if it exists
            if (existingAdditionalData.rejection_history) {
                devLog('Preserving existing rejection_history:', existingAdditionalData.rejection_history);
            }

            // Prepare additional data from form fields with proper nesting
            let additionalData = { ...existingAdditionalData };
            
            // Merge new paths properly if any staged files were uploaded
            additionalData = mergeDottedPaths(additionalData, newFilePaths);
            
            // Get all additional fields from the database to know which fields to include
            devLog('Fetching additional fields...');
            const { data: additionalFieldsList, error: fieldsError } = await supabase
                .from('additional_registration_fields')
                .select('field_key')
                .or('field_status.is.null,field_status.eq.active')
                .eq('is_original_field', false);

            if (fieldsError) {
                console.error('Error fetching additional fields:', fieldsError);
                throw new Error(`Failed to fetch additional fields: ${fieldsError.message}`);
            }

            devLog('Additional fields:', additionalFieldsList);

            // Extract additional field values from formData with nesting
            if (additionalFieldsList) {
                additionalFieldsList.forEach(field => {
                    const fieldValue = getFieldValue(formData, field.field_key);
                    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
                        // Handle nested field keys (e.g., "personal_details.gender")
                        const keys = field.field_key.split('.');
                        if (keys.length > 1) {
                            // Create nested structure
                            let current = additionalData;
                            for (let i = 0; i < keys.length - 1; i++) {
                                if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                                    current[keys[i]] = {};
                                }
                                current = current[keys[i]];
                            }
                            current[keys[keys.length - 1]] = fieldValue;
                        } else {
                            additionalData[field.field_key] = fieldValue;
                        }
                    }
                });
            }

            // Preserve rejection_history from existing additional_data
            if (existingAdditionalData.rejection_history) {
                additionalData.rejection_history = existingAdditionalData.rejection_history;
            }

            if (formData.taking_course_in_singapore === 'yes' || formData.taking_course_in_singapore === 'no') {
                additionalData.taking_course_in_singapore = formData.taking_course_in_singapore;
            } else {
                delete additionalData.taking_course_in_singapore;
            }

            // Add declaration date (today's date) to additional data
            const today = new Date().toISOString().split('T')[0];
            if (!additionalData.legal) {
                additionalData.legal = {};
            }
            additionalData.legal.declaration_date = today;

            // Save signature if provided
            if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
                const signatureDataUrl = signaturePadRef.current.toDataURL();
                // Store signature using the database field key format
                additionalData.legal_declaration_signature_url = signatureDataUrl;
                // Store signature timestamp (display format requested)
                const signedAt = moment().format('DD MMM YYYY HH:mm:ss');
                additionalData.legal_declaration_signature_timestamp = `Signed at: ${signedAt}`;
                // Also store an ISO timestamp for auditing/debugging if needed
                additionalData.legal_declaration_signature_timestamp_iso = new Date().toISOString();
                devLog('Signature saved to additional_data');
            }

            devLog('Additional data prepared:', additionalData);

            // Determine registration stage: "amended" if there was a rejection_reason, otherwise "pending"
            const registrationStage = hasRejectionReason ? "amended" : "pending";
            
            if (hasRejectionReason) {
                devLog('Registration has rejection_reason, setting stage to "amended"');
            }

            // Update the single registration with all data
            const updateData = {
                participant_name: formData.participant_name,
                participant_email: formData.participant_email,
                nationality: formData.nationality,
                nric: formData.nric,
                dob: formData.dob,
                birth_age: formData.birth_age,
                mobile_no: formData.mobile_no,
                registration_stage: registrationStage,
                draft_cert_run_code: formData.draft_cert_run_code,
                additional_data: additionalData, 
                rejection_reason: null // Clear rejection_reason when resubmitting
            };

            devLog('Updating registration with data:', updateData);

            const {data, error} = await supabase
                .from('registration')
                .update(updateData)
                .eq('id', registrationId)
                .select("id,participant_email")
                .single();

            devLog('Update result:', {data, error});

            if (error) {
                console.error('Registration error:', error);
                setIsSubmitting(false);
                toast({
                    title: "Registration Failed",
                    description: `Failed to update registration. Error: ${error.message}. Please try again.`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                // Pending registration-fee invoice (service role via edge function; anon cannot insert invoices).
                createPendingRegistrationFeeInvoice(supabase, registrationId).catch((err) => {
                    console.error('Registration fee invoice creation failed:', err);
                });
                setIsSubmitting(false);

                // Show appropriate success message based on registration stage
                const successMessage = hasRejectionReason 
                    ? "Your registration has been resubmitted as 'Amended' and will be reviewed again."
                    : "Your registration has been submitted successfully!";

                toast({
                    title: hasRejectionReason ? "Registration Resubmitted" : "Registration Successful",
                    description: successMessage,
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });

                // Clear locally-saved draft once the data is successfully persisted.
                clearLocalDraft();

                // Handle payment options
                if (formData.payment_option === 'pay_now' || formData.payment_option === 'bank_transfer') {
                    // Show payment modal for PayNow or Bank Transfer
                    setShowPayNowModal(true);
                    setIsSubmitting(false);
                    return; // Stop here, showing the modal
                } else if (formData.payment_option === 'stripe') {
                    // Handle Stripe payment directly
                    handleStripePayment();
                    setIsSubmitting(false);
                    return; // Stop here, Stripe will redirect
                }

                // Navigate to pending page (for pay_later or other options)
                navigate(`/registration-pending/${registrationId}`);
            }
        } catch (error) {
            console.error('Unexpected error during registration:', error);
            setIsSubmitting(false);
            toast({
                title: "Registration Failed",
                description: `Registration failed due to an unexpected error: ${error.message}. Please try again.`,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    // Get certification info
    const params = new URLSearchParams(location.search);
    const fullCertification = params.get('full_certification');
    const selectedCertRun = courseRuns.find(course => course.id === formData.draft_cert_run_code);
    const selectedCourseRun = selectedCertRun?.full_cert_many_course_run;
    const selectedCertType = selectedCertRun?.full_certification;
    const payNowReference = buildPayNowReference({
        programmeCode: selectedCertType?.code,
        intake: selectedCertRun?.intake,
        participantName: formData.participant_name,
    });
    console.log(selectedCourseRun, "selectedCourseRun")

    // Determine if form should be read-only (only editable when stage is "draft" or "rejected")
    const isReadOnly = !!formData.registration_stage &&
                       formData.registration_stage !== "draft" &&
                       formData.registration_stage !== "rejected";

    // Lock full course and intake selection once participant is past draft (e.g. pending, approved, etc.)
    const isCourseAndIntakeLocked = formData.registration_stage != null && formData.registration_stage !== "draft";

    // Saved signature from additional_data (used for read-only static display)
    const savedSignatureUrl =
        typeof formData.legal_declaration_signature_url === 'string' &&
        formData.legal_declaration_signature_url.startsWith('data:image/')
            ? formData.legal_declaration_signature_url
            : null;
    const showSignatureCaptured = isReadOnly ? !!savedSignatureUrl : !isSignatureEmpty;

    return (
        <div>
            {/* Help Popup - shows after 5 minutes of inactivity */}
            <HelpPopup pageName="Course Registration" />

            {/* Floating Progress Bar - Always at top of viewport */}
            {!loadingCourseRuns && !loadingRegistration && !isReadOnly && formData.draft_cert_run_code && (
                <FloatingProgressBar 
                    filledCount={progressData.filledCount}
                    totalCount={progressData.totalCount}
                    emailVerified={emailVerified}
                    emailVerificationSent={emailVerificationSent}
                    participantEmail={formData.participant_email}
                    onResendVerification={resendVerificationEmail}
                    isSendingVerification={isSendingVerification}
                    showEmailVerification={isInitialSaveComplete}
                />
            )}

            {(loadingCourseRuns || loadingRegistration) ? (
                <Center h="60vh">
                    <VStack spacing={4}>
                        <Spinner size="xl" thickness="4px" speed="0.65s" color="brand.600"/>
                        <Text color="gray.600">
                            {loadingRegistration ? "Loading registration..." : "Loading courses..."}
                        </Text>
                    </VStack>
                </Center>
            ) : (
                <Stack
                    spacing={{base: 4, md: 5}}
                    width="100%"
                    bg={"white"}
                    px={{base: 4, md: 8, lg: 10}}
                    pt={{base: formData.draft_cert_run_code && !isReadOnly ? "80px" : 5, md: formData.draft_cert_run_code && !isReadOnly ? "90px" : 8}} // Account for floating progress bar
                    pb={{base: "120px", md: "100px"}} // Extra bottom padding for fixed action button
                    borderRadius={{base: "lg", md: "xl"}}
                    boxShadow={{base: "sm", md: "lg"}}
                    mx={0}
                >
                    {/* Registration Status Banner */}
                    {isReadOnly && formData.registration_stage && (
                        <Alert 
                            status={
                                formData.registration_stage === "approved" ? "success" :
                                formData.registration_stage === "amended" ? "info" :
                                "warning"
                            } 
                            mb={6} 
                            borderRadius="md"
                            variant="left-accent"
                        >
                            <AlertIcon />
                            <Box flex="1">
                                <Text fontSize={{base: "xs", md: "sm"}} fontWeight="bold" mb={1}>
                                    Registration Status: {formData.registration_stage.charAt(0).toUpperCase() + formData.registration_stage.slice(1)}
                                </Text>
                                <Text fontSize={{base: "10px", md: "xs"}}>
                                    {formData.registration_stage === "approved" 
                                        ? "Your registration has been approved. This form is now read-only."
                                        : formData.registration_stage === "amended"
                                        ? "Your registration has been resubmitted and is under review. This form is read-only."
                                        : "This registration is no longer editable."}
                                </Text>
                            </Box>
                        </Alert>
                    )}

                    {formData.rejection_reason && (
                        <Alert status="error" mb={4} borderRadius="md" bg="red.50" borderColor="red.200" borderWidth="1px">
                            <AlertIcon color="red.500" />
                            <Box>
                                <Text fontSize={{base: "xs", md: "sm"}} fontWeight="bold" color="red.700">
                                    Registration Rejected
                                </Text>
                                <Text fontSize={{base: "10px", md: "xs"}} mt={1} color="red.600">
                                    <strong>Reason:</strong> {formData.rejection_reason}
                                </Text>
                                <Text fontSize={{base: "10px", md: "xs"}} mt={2} color="red.600">
                                    💡 Please address the issues above and resubmit. Your registration will be marked as "Amended" for review.
                                </Text>
                            </Box>
                        </Alert>
                    )}

                    <Box w={"100%"} borderRadius="md" p={{base: 4, md: 6}}>
                        {/* Welcome placeholder - Show when no cert run is selected */}
                        {!formData.draft_cert_run_code && (
                            <>
                                <Box 
                                    mb={6} 
                                    p={{base: 6, md: 8}} 
                                    bg="gradient-to-r"
                                    bgGradient="linear(to-r, blue.50, purple.50)"
                                    borderRadius="xl" 
                                    borderWidth="2px" 
                                    borderColor="blue.200"
                                    textAlign="center"
                                >
                                    <VStack spacing={4}>
                                        <Box fontSize={{base: "3xl", md: "4xl"}}>🎓</Box>
                                        <Heading size={{base: "sm", md: "md"}} color="gray.700">
                                            {getTenantBranding().shortName
                                                ? `Welcome to ${getTenantBranding().shortName}`
                                                : "Welcome"}
                                        </Heading>
                                        <Text fontSize={{base: "xs", md: "sm"}} color="gray.600" maxW="600px">
                                            Get started by selecting your preferred certification run below. 
                                            Once selected, you'll see programme details, schedule, and the registration form.
                                        </Text>
                                    </VStack>
                                </Box>

                                {/* Required Items Instruction Card - Show at the start */}
                                <Box
                                    mb={6}
                                    p={{base: 4, md: 5}}
                                    bg="orange.50"
                                    borderRadius="lg"
                                    borderWidth="1px"
                                    borderColor="orange.200"
                                    boxShadow="sm"
                                >
                                    <VStack align="stretch" spacing={4}>
                                        <HStack spacing={2}>
                                            <Icon as={HiDocumentText} boxSize={{base: 5, md: 6}} color="orange.600" />
                                            <Heading size={{base: "xs", md: "sm"}} color="gray.800">
                                                Required Items
                                            </Heading>
                                        </HStack>
                                        <Text fontSize={{base: "xs", md: "sm"}} color="gray.600" mb={2}>
                                            Please prepare the following items before starting your registration. You'll need them during the process.
                                        </Text>
                                        <SimpleGrid columns={{base: 1, sm: 2, lg: 4}} spacing={{base: 3, md: 4}}>
                                            {/* NRIC/FIN & Passport */}
                                            <Box
                                                p={{base: 3, md: 4}}
                                                bg="white"
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor="orange.200"
                                                _hover={{borderColor: "orange.400", boxShadow: "sm"}}
                                                transition="all 0.2s"
                                            >
                                                <VStack align="start" spacing={2}>
                                                    <HStack spacing={2}>
                                                        <Box
                                                            p={2}
                                                            bg="green.100"
                                                            borderRadius="md"
                                                        >
                                                            <Icon as={HiDocumentText} boxSize={{base: 4, md: 5}} color="green.600" />
                                                        </Box>
                                                        <Text fontSize={{base: "xs", md: "sm"}} fontWeight="semibold" color="gray.800">
                                                            NRIC/FIN & Passport
                                                        </Text>
                                                    </HStack>
                                                    <Text fontSize={{base: "2xs", md: "xs"}} color="gray.600" lineHeight="1.5">
                                                        Clear photo or scan of your valid NRIC/FIN (front & back) or Passport (information page)
                                                    </Text>
                                                </VStack>
                                            </Box>

                                            {/* Education Certificate / CV */}
                                            <Box
                                                p={{base: 3, md: 4}}
                                                bg="white"
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor="orange.200"
                                                _hover={{borderColor: "orange.400", boxShadow: "sm"}}
                                                transition="all 0.2s"
                                            >
                                                <VStack align="start" spacing={2}>
                                                    <HStack spacing={2}>
                                                        <Box
                                                            p={2}
                                                            bg="blue.100"
                                                            borderRadius="md"
                                                        >
                                                            <Icon as={HiAcademicCap} boxSize={{base: 4, md: 5}} color="blue.600" />
                                                        </Box>
                                                        <Text fontSize={{base: "xs", md: "sm"}} fontWeight="semibold" color="gray.800">
                                                            Educational Certificates / Updated CV
                                                        </Text>
                                                    </HStack>
                                                    <Text fontSize={{base: "2xs", md: "xs"}} color="gray.600" lineHeight="1.5">
                                                        Highest qualification certificate or your latest professional resume
                                                    </Text>
                                                </VStack>
                                            </Box>

                                            {/* Recent Photo */}
                                            <Box
                                                p={{base: 3, md: 4}}
                                                bg="white"
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor="orange.200"
                                                _hover={{borderColor: "orange.400", boxShadow: "sm"}}
                                                transition="all 0.2s"
                                            >
                                                <VStack align="start" spacing={2}>
                                                    <HStack spacing={2}>
                                                        <Box
                                                            p={2}
                                                            bg="purple.100"
                                                            borderRadius="md"
                                                        >
                                                            <Icon as={HiCamera} boxSize={{base: 4, md: 5}} color="purple.600" />
                                                        </Box>
                                                        <Text fontSize={{base: "xs", md: "sm"}} fontWeight="semibold" color="gray.800">
                                                            Recent Photo
                                                        </Text>
                                                    </HStack>
                                                    <Text fontSize={{base: "2xs", md: "xs"}} color="gray.600" lineHeight="1.5">
                                                        A clear passport-sized photograph taken within the last 6 months
                                                    </Text>
                                                </VStack>
                                            </Box>

                                            {/* Email & Phone */}
                                            <Box
                                                p={{base: 3, md: 4}}
                                                bg="white"
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor="orange.200"
                                                _hover={{borderColor: "orange.400", boxShadow: "sm"}}
                                                transition="all 0.2s"
                                            >
                                                <VStack align="start" spacing={2}>
                                                    <HStack spacing={2}>
                                                        <Box
                                                            p={2}
                                                            bg="orange.100"
                                                            borderRadius="md"
                                                        >
                                                            <Icon as={HiDeviceMobile} boxSize={{base: 4, md: 5}} color="orange.600" />
                                                        </Box>
                                                        <Text fontSize={{base: "xs", md: "sm"}} fontWeight="semibold" color="gray.800">
                                                            Valid Email & Phone Number
                                                        </Text>
                                                    </HStack>
                                                    <Text fontSize={{base: "2xs", md: "xs"}} color="gray.600" lineHeight="1.5">
                                                        Ensure you have access to your email and phone for verification
                                                    </Text>
                                                </VStack>
                                            </Box>
                                        </SimpleGrid>
                                    </VStack>
                                </Box>
                            </>
                        )}

                        {/* Certification Type Info - Show when a cert run is selected */}
                        {selectedCertType && formData.draft_cert_run_code && (() => {
                            // Use discount from cert run if available, otherwise fall back to cert type discount
                            const discount = selectedCertRun?.discount !== undefined && selectedCertRun?.discount !== null 
                                ? parseFloat(selectedCertRun.discount || 0)
                                : (selectedCertType.discount !== undefined && selectedCertType.discount !== null 
                                    ? parseFloat(selectedCertType.discount || 0)
                                    : 0);
                            const cost = selectedCertType.cost !== undefined && selectedCertType.cost !== null 
                                ? parseFloat(selectedCertType.cost || 0)
                                : 0;
                            
                            // GST calculation (org settings + location-based GST exemption)
                            const subtotal = cost - discount;
                            const applyGst = getEffectiveGstEnabled();
                            const gstRate = applyGst ? (orgGstSettings.gst_rate / 100) : 0;
                            const gstAmount = subtotal * gstRate;
                            const totalPayable = subtotal + gstAmount;
                            const gstPercentDisplay = applyGst ? orgGstSettings.gst_rate : 0;
                            
                            return (
                                <Box 
                                    mb={5} 
                                    p={{base: 4, md: 5}} 
                                    bg="blue.50" 
                                    borderRadius="lg" 
                                    borderWidth="1px" 
                                    borderColor="blue.200"
                                    mt={-2}
                                >
                                    <VStack align="start" spacing={3}>
                                        {selectedCertType.description && (
                                            <Box>
                                                <Text fontSize={{base: "xs", md: "sm"}} fontWeight="semibold" color="gray.700" mb={2}>
                                                    Programme Description
                                                </Text>
                                                <Text fontSize={{base: "xs", md: "sm"}} color="gray.600" lineHeight="1.6">
                                                    {selectedCertType.description}
                                                </Text>
                                            </Box>
                                        )}
                                        {cost > 0 && (
                                            <Box 
                                                w="100%" 
                                                bg="white" 
                                                p={{base: 3, md: 4}} 
                                                borderRadius="md" 
                                                boxShadow="sm"
                                            >
                                                <VStack spacing={2} align="stretch">
                                                    {/* Programme Fee */}
                                                    <Flex justify="space-between" align="center">
                                                        <Text fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                            Programme Fee
                                                        </Text>
                                                        <Text fontSize={{base: "sm", md: "md"}} fontWeight="medium" color="gray.800">
                                                            S${cost.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Text>
                                                    </Flex>
                                                    
                                                    {/* Discount (if applicable) */}
                                                    {discount > 0 && (
                                                        <Flex justify="space-between" align="center">
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="green.600">
                                                                Discount
                                                            </Text>
                                                            <Text fontSize={{base: "sm", md: "md"}} fontWeight="medium" color="green.600">
                                                                -S${discount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                    
                                                    {/* Subtotal (before GST) - only show if there's a discount */}
                                                    {discount > 0 && (
                                                        <Flex justify="space-between" align="center" pt={1} borderTop="1px dashed" borderColor="gray.200">
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                                Subtotal
                                                            </Text>
                                                            <Text fontSize={{base: "sm", md: "md"}} fontWeight="medium" color="gray.800">
                                                                S${subtotal.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                    
                                                    {/* GST - only show if enabled for this student */}
                                                    {applyGst && gstAmount > 0 && (
                                                        <Flex justify="space-between" align="center">
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                                GST ({gstPercentDisplay}%)
                                                            </Text>
                                                            <Text fontSize={{base: "sm", md: "md"}} fontWeight="medium" color="gray.700">
                                                                S${gstAmount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                    
                                                    {/* Total Payable */}
                                                    <Flex 
                                                        justify="space-between" 
                                                        align="center" 
                                                        pt={2} 
                                                        mt={1}
                                                        borderTop="2px solid" 
                                                        borderColor="brand.400"
                                                    >
                                                        <Text fontSize={{base: "sm", md: "md"}} fontWeight="bold" color="gray.800">
                                                            Total Payable
                                                            {applyGst && gstAmount > 0 && (
                                                                <Text as="span" fontSize="xs" fontWeight="normal" color="gray.500" ml={1}>
                                                                    (incl. GST)
                                                                </Text>
                                                            )}
                                                        </Text>
                                                        <Text fontSize={{base: "lg", md: "xl"}} fontWeight="bold" color="brand.600">
                                                            S${totalPayable.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Text>
                                                    </Flex>
                                                </VStack>
                                            </Box>
                                        )}
                                    </VStack>
                                </Box>
                            );
                        })()}

                        {/* Certification Selection - Two Step Process */}
                        <VStack spacing={4} align="stretch" mb={5}>
                            {/* Step 1: Select Certification Name */}
                            <FormControl>
                                <FormLabel mb={2} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                    Select Course
                                    {!selectedCertName && (
                                        <Badge ml={2} colorScheme="blue" fontSize="xs">
                                            Step 1
                                        </Badge>
                                    )}
                                </FormLabel>
                                <LightMode>
                                    <SearchableSelect
                                        value={
                                            selectedCertName
                                                ? {
                                                    value: selectedCertName,
                                                    label: groupedCourseRuns[selectedCertName]?.name
                                                        || courseRuns[0]?.full_certification?.name
                                                        || 'Unknown Certification',
                                                }
                                                : null
                                        }
                                        placeholder="Search and select a course..."
                                        options={
                                            Object.keys(groupedCourseRuns).length > 0
                                                ? Object.keys(groupedCourseRuns)
                                                    .sort((a, b) => groupedCourseRuns[a].name.localeCompare(groupedCourseRuns[b].name))
                                                    .map((certTypeKey) => ({
                                                        value: certTypeKey,
                                                        label: groupedCourseRuns[certTypeKey].name,
                                                    }))
                                                : courseRuns.length > 0
                                                    ? [{
                                                        value: courseRuns[0]?.full_certification?.code || courseRuns[0]?.full_cert_type_code || '',
                                                        label: courseRuns[0]?.full_certification?.name || 'Unknown Certification',
                                                    }]
                                                    : []
                                        }
                                        onChange={(option) => {
                                            if (isCourseAndIntakeLocked) return;
                                            const certKey = option?.value || '';
                                            setSelectedCertName(certKey);
                                            handleChange("draft_cert_run_code", '');
                                        }}
                                        isDisabled={isReadOnly || isCourseAndIntakeLocked}
                                        isClearable={!isReadOnly && !isCourseAndIntakeLocked}
                                        selectedOptionStyle="check"
                                        chakraStyles={{
                                            control: (provided) => ({
                                                ...provided,
                                                bg: isReadOnly || isCourseAndIntakeLocked ? "gray.50" : "white",
                                                minHeight: "48px",
                                            }),
                                            menu: (provided) => ({
                                                ...provided,
                                                bg: "white",
                                                zIndex: 10,
                                            }),
                                            menuList: (provided) => ({
                                                ...provided,
                                                bg: "white",
                                            }),
                                            option: (provided, state) => ({
                                                ...provided,
                                                bg: state.isFocused ? "blue.50" : "white",
                                                color: "gray.800",
                                            }),
                                            singleValue: (provided) => ({
                                                ...provided,
                                                color: "gray.800",
                                            }),
                                            input: (provided) => ({
                                                ...provided,
                                                color: "gray.800",
                                            }),
                                            placeholder: (provided) => ({
                                                ...provided,
                                                color: "gray.500",
                                            }),
                                        }}
                                    />
                                </LightMode>
                                {Object.keys(groupedCourseRuns).length === 0 && courseRuns.length === 0 && (
                                    <Alert status="info" mt={2} borderRadius="md">
                                        <AlertIcon />
                                        <Text fontSize="sm">
                                            No certifications available at the moment. Please check back later or contact us for more information.
                                        </Text>
                                    </Alert>
                                )}
                            </FormControl>

                            {/* Step 2: Select Upcoming Batch */}
                            {selectedCertName && groupedCourseRuns[selectedCertName] && (
                                <FormControl>
                                    <FormLabel mb={4} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                        Select Upcoming Batch
                                        <Badge ml={2} colorScheme="green" fontSize="xs">
                                            Step 2
                                        </Badge>
                                    </FormLabel>
                                    {!formData.draft_cert_run_code && (
                                        <Text mb={3} fontSize="sm" color="gray.600">
                                            Click a batch below to choose your schedule
                                        </Text>
                                    )}
                                    {groupedCourseRuns[selectedCertName].runs.length === 0 ? (
                                        <Alert status="warning" mt={2} borderRadius="md">
                                            <AlertIcon />
                                            <Text fontSize="sm">
                                                No course runs available for this course.
                                            </Text>
                                        </Alert>
                                    ) : (
                                        <SimpleGrid columns={{base: 1, md: 2, lg: 3}} spacing={{base: 3, md: 4}}>
                                            {(() => {
                                                const runs = groupedCourseRuns[selectedCertName].runs;
                                                return runs.map((course) => {
                                                    const startDate = moment(course.start_date).format('D MMM, YYYY');
                                                    const endDate = moment(course.end_date).format('D MMM, YYYY');
                                                    const runName = course?.intake ? `Batch ${course.intake}` : 'Unnamed Run';
                                                    const isSelected = formData.draft_cert_run_code === course.id;
                                                    const isDisabled = isReadOnly || isCourseAndIntakeLocked;
                                                    
                                                    return (
                                                        <Box
                                                            key={course.id}
                                                            as="button"
                                                            type="button"
                                                            onClick={() => {
                                                                if (!isDisabled) {
                                                                    handleChange("draft_cert_run_code", course.id);
                                                                }
                                                            }}
                                                            disabled={isDisabled}
                                                            borderWidth="2px"
                                                            borderRadius="xl"
                                                            p={{base: 3, md: 4}}
                                                            bg={isSelected ? "blue.50" : "white"}
                                                            borderColor={isSelected ? "blue.500" : "gray.300"}
                                                            boxShadow={isSelected ? "md" : "sm"}
                                                            transition="all 0.2s"
                                                            cursor={isDisabled ? "not-allowed" : "pointer"}
                                                            textAlign="left"
                                                            position="relative"
                                                            minH={{base: "120px", md: "auto"}}
                                                            _hover={!isDisabled ? {
                                                                borderColor: "blue.400",
                                                                boxShadow: "lg",
                                                                transform: "translateY(-2px)",
                                                                bg: isSelected ? "blue.50" : "gray.50"
                                                            } : {}}
                                                            _focus={{ outline: "none" }}
                                                            _focusVisible={{
                                                                outline: "2px solid",
                                                                outlineColor: "blue.400",
                                                                outlineOffset: "2px"
                                                            }}
                                                            _disabled={{
                                                                opacity: 0.5,
                                                                cursor: "not-allowed"
                                                            }}
                                                        >
                                                            {isSelected ? (
                                                                <Badge
                                                                    position="absolute"
                                                                    top={2}
                                                                    right={2}
                                                                    colorScheme="blue"
                                                                    fontSize="xs"
                                                                >
                                                                    Selected
                                                                </Badge>
                                                            ) : !isDisabled ? (
                                                                <Icon
                                                                    as={FaHandPointer}
                                                                    position="absolute"
                                                                    top={3}
                                                                    right={3}
                                                                    boxSize={5}
                                                                    color="gray.400"
                                                                    aria-hidden
                                                                />
                                                            ) : null}
                                                            <VStack align="stretch" spacing={2} pr={isSelected ? 0 : 6}>
                                                                <HStack spacing={2} align="center">
                                                                    <Text
                                                                        fontSize={{base: "sm", md: "md"}}
                                                                        fontWeight="700"
                                                                        color="gray.800"
                                                                        lineHeight="1.4"
                                                                    >
                                                                        {runName}
                                                                    </Text>
                                                                    {course.id && (
                                                                        <Badge
                                                                            colorScheme="purple"
                                                                            fontSize="2xs"
                                                                            variant="subtle"
                                                                            flexShrink={0}
                                                                        >
                                                                            {course.id}
                                                                        </Badge>
                                                                    )}
                                                                </HStack>
                                                                <HStack spacing={2} fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                                    <Text fontWeight="600">Start:</Text>
                                                                    <Text>{startDate}</Text>
                                                                </HStack>
                                                                <HStack spacing={2} fontSize={{base: "xs", md: "sm"}} color="gray.600">
                                                                    <Text fontWeight="600">End:</Text>
                                                                    <Text>{endDate}</Text>
                                                                </HStack>
                                                            </VStack>
                                                        </Box>
                                                    );
                                                });
                                            })()}
                                        </SimpleGrid>
                                    )}
                                </FormControl>
                            )}
                        </VStack>

                        {/* Display Selected Modules - hidden per client request
                        {selectedCourseRun && (
                            <Box mb={6}>
                                <Flex justify="space-between" align="center" mb={2}>
                                    <FormLabel m={0} fontWeight="bold" color="black" fontSize={{base: "sm", md: "md"}}>
                                        Course Modules
                                        <Badge ml={2} colorScheme="green" fontSize="xs">
                                            {selectedCourseRun.length} {selectedCourseRun.length === 1 ? 'Module' : 'Modules'}
                                        </Badge>
                                    </FormLabel>
                                    <Button 
                                        size="xs" 
                                        variant="ghost" 
                                        colorScheme="brand" 
                                        onClick={() => setShowModules(!showModules)}
                                        rightIcon={showModules ? <HiChevronUp /> : <HiChevronDown />}
                                    >
                                        {showModules ? "Hide" : "View Modules"}
                                    </Button>
                                </Flex>
                                
                                {showModules && (
                                    <SimpleGrid columns={{base: 1, md: 2, lg: 3}} spacing={{base: 4, md: 6}} mt={4}>
                                        {selectedCourseRun
                                            ?.slice()
                                            .sort((a, b) => {
                                                const getSortDate = (course) => {
                                                    const courseRun = course?.course_run;
                                                    if (courseRun?.course_dates && courseRun.course_dates.length > 0) {
                                                        return new Date(courseRun.course_dates[0].date);
                                                    }
                                                    return courseRun?.start_date ? new Date(courseRun.start_date) : new Date('9999-12-31');
                                                };
                                                const dateA = getSortDate(a);
                                                const dateB = getSortDate(b);
                                                return dateA - dateB;
                                            })
                                            .map((course) => (
                                                <ModuleCard
                                                    key={course.id}
                                                    course_name={course?.course_run?.course_type.name}
                                                    start_date={course?.course_run?.start_date}
                                                    end_date={course?.course_run?.end_date}
                                                    course_dates={course?.course_run?.course_dates}
                                                    course_type_code={course?.course_run?.course_type_code}
                                                    module_tag={course?.course_run?.module_tag}
                                                />
                                            ))}
                                    </SimpleGrid>
                                )}
                            </Box>
                        )}
                        */}

                                {/* Only show form if certification run is selected */}
                        {formData.draft_cert_run_code && (
                            <>
                                {/* Information banner - removed two-step process */}

                                {/* Compulsory Fields Section - ALL required fields (basic + custom) */}
                                {(basicFieldsOrder.length > 0 || compulsoryCustomFields.length > 0) && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: 0.1 }}
                                    >
                                    <Box mt={{base: 4, md: 6}} mb={4}>
                                        <Box 
                                            mb={4} 
                                            p={{base: 3, md: 4}} 
                                            bg="red.50" 
                                            borderRadius="lg" 
                                            borderLeft="4px solid" 
                                            borderColor="red.500"
                                        >
                                            <HStack spacing={2}>
                                                <Text fontSize={{base: "md", md: "lg"}} fontWeight="bold" color="red.700">
                                                    ⭐ Compulsory Fields
                                                </Text>
                                            </HStack>
                                            <Text fontSize={{base: "xs", md: "sm"}} color="red.600" mt={1}>
                                                Please complete all required fields to continue
                                            </Text>
                                        </Box>
                                        <VStack spacing={{base: 3, md: 4}} align="stretch">
                                            {/* Basic required fields (from database order) */}
                                            {basicFieldsOrder.map((field) => renderBasicField(field.field_key))}
                                            
                                            {/* Custom required fields */}
                                            {compulsoryCustomFields.map((field) => renderCompulsoryCustomField(field))}
                                        </VStack>
                                    </Box>
                                    </motion.div>
                                )}

                                {/* Email verification is now shown in the floating progress bar at top */}

                                {/* Additional Fields Section - Show all fields at once */}
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.2 }}
                                >
                                <Box ref={additionalFieldsRef} mt={{base: 6, md: 8}}>
                                    {/* Progress indicator */}
                                    <Box 
                                        mb={4} 
                                        p={{base: 3, md: 4}} 
                                        bg="blue.50" 
                                        borderRadius="lg" 
                                        borderLeft="4px solid" 
                                        borderColor="blue.500"
                                    >
                                        <HStack spacing={2}>
                                            <Text fontSize={{base: "md", md: "lg"}} fontWeight="bold" color="blue.700">
                                                📋 Additional Information
                                            </Text>
                                        </HStack>
                                        <Text fontSize={{base: "xs", md: "sm"}} color="blue.600" mt={1}>
                                            Complete all sections below to finalise your registration
                                        </Text>
                                    </Box>
                                    
                                    <Box mt={{base: 3, md: 4}}>
                                        <AdditionalFields
                                            formData={formData}
                                            setFormData={setFormData}
                                            validationErrors={validationErrors}
                                            setValidationErrors={setValidationErrors}
                                            registrationId={registrationId}
                                            setRegistrationId={setRegistrationId}
                                            isReadOnly={isReadOnly}
                                            selectedFiles={selectedFiles}
                                            setSelectedFiles={setSelectedFiles}
                                            uploadingFiles={uploadingFiles}
                                            setUploadingFiles={setUploadingFiles}
                                            onAutoSave={async () => {
                                                // Auto-save a draft registration if needed for file uploads
                                                if (!registrationId && formData.participant_name && formData.participant_email && formData.draft_cert_run_code) {
                                                    try {
                                                        const registrationData = {
                                                            participant_name: formData.participant_name,
                                                            participant_email: formData.participant_email,
                                                            nationality: formData.nationality || '',
                                                            nric: formData.nric || '',
                                                            dob: formData.dob || '',
                                                            birth_age: formData.birth_age || '',
                                                            mobile_no: formData.mobile_no || '',
                                                            registration_stage: "draft",
                                                            draft_cert_run_code: formData.draft_cert_run_code,
                                                        };
                                                        
                                                        const { data, error } = await supabase
                                                            .from('registration')
                                                            .insert([registrationData])
                                                            .select("id")
                                                            .single();
                                                        
                                                        if (error) throw error;
                                                        
                                                        const savedId = data.id;
                                                        setRegistrationId(savedId);
                                                        setIsInitialSaveComplete(true);
                                                        
                                                        // Send verification email
                                                        if (formData.participant_email) {
                                                            await sendVerificationEmail(savedId, formData.participant_email, formData.participant_name);
                                                        }
                                                        
                                                        return savedId;
                                                    } catch (error) {
                                                        console.error('Auto-save error:', error);
                                                        toast({
                                                            title: "Auto-save Failed",
                                                            description: "Could not save registration. Please fill in all required fields.",
                                                            status: "error",
                                                            duration: 5000,
                                                            isClosable: true,
                                                        });
                                                        return null;
                                                    }
                                                }
                                                return null;
                                            }}
                                        />
                                    </Box>
                                </Box>
                                </motion.div>

                                {/* Privacy Policy, Terms & Conditions, and Signature Section - SHOW IMMEDIATELY AFTER INITIAL SAVE */}
                                {isInitialSaveComplete && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <Box mt={8} mb={6}>
                                            <VStack spacing={6} align="stretch">
                                                {/* Privacy Policy Section */}
                                                <Box
                                                    bg="white"
                                                    p={{base: 4, md: 6}}
                                                    borderRadius="lg"
                                                    boxShadow="sm"
                                                    borderWidth="1px"
                                                    borderColor="gray.200"
                                                    borderLeft="4px solid"
                                                    borderLeftColor="blue.500"
                                                >
                                                    <HStack mb={4} spacing={3}>
                                                        <Box
                                                            bg="blue.50"
                                                            p={2}
                                                            borderRadius="md"
                                                        >
                                                            <Text fontSize="xl">🔒</Text>
                                                        </Box>
                                                        <Heading 
                                                            size={{base: "sm", md: "md"}} 
                                                            color="gray.800"
                                                            fontWeight="bold"
                                                        >
                                                            Privacy Policy
                                                        </Heading>
                                                    </HStack>
                                                    <Text 
                                                        fontSize={{base: "xs", md: "sm"}} 
                                                        color="gray.700" 
                                                        lineHeight="1.8" 
                                                        mb={4}
                                                    >
                                                        In line with the Personal Data Protection Act, ECMT would like to inform you on the collection, use and disclosure of all applicants' and trainees' personal data.
                                                    </Text>
                                                    <VStack align="start" spacing={3} pl={{base: 2, md: 4}}>
                                                        <HStack align="start" spacing={2}>
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.600" fontWeight="bold" minW="20px">1.</Text>
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.700" lineHeight="1.8">
                                                                Personal data (as required below) will be collected, used and/or disclosed during the registration and administration of courses.
                                                            </Text>
                                                        </HStack>
                                                        <HStack align="start" spacing={2}>
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.600" fontWeight="bold" minW="20px">2.</Text>
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.700" lineHeight="1.8">
                                                                Personal data collected is for the following purposes: Requirement of a unique identity for course registration
                                                            </Text>
                                                        </HStack>
                                                        <HStack align="start" spacing={2}>
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.600" fontWeight="bold" minW="20px">3.</Text>
                                                            <Text fontSize={{base: "xs", md: "sm"}} color="gray.700" lineHeight="1.8">
                                                                Contact Person's Information: Primary point of contact for receiving course status updates, invoices, and all official correspondence.
                                                            </Text>
                                                        </HStack>
                                                    </VStack>
                                                </Box>

                                                {/* Terms and Conditions Section */}
                                                <Box
                                                    bg="white"
                                                    p={{base: 4, md: 6}}
                                                    borderRadius="lg"
                                                    boxShadow="sm"
                                                    borderWidth="1px"
                                                    borderColor="gray.200"
                                                    borderLeft="4px solid"
                                                    borderLeftColor="green.500"
                                                >
                                                    <HStack mb={4} spacing={3}>
                                                        <Box
                                                            bg="green.50"
                                                            p={2}
                                                            borderRadius="md"
                                                        >
                                                            <Text fontSize="xl">📋</Text>
                                                        </Box>
                                                        <Heading 
                                                            size={{base: "sm", md: "md"}} 
                                                            color="gray.800"
                                                            fontWeight="bold"
                                                        >
                                                            Terms and Conditions
                                                        </Heading>
                                                    </HStack>
                                                    <Text 
                                                        fontSize={{base: "xs", md: "sm"}} 
                                                        color="gray.700" 
                                                        lineHeight="1.8"
                                                    >
                                                        Kindly refer to{' '}
                                                        <Text 
                                                            as="span" 
                                                            color="blue.600"
                                                            fontWeight="medium"
                                                            _hover={{ textDecoration: 'underline' }}
                                                        >
                                                            <a 
                                                                href="https://edusphere.edu.sg/essential-resources-and-support-for-students/" 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                style={{ textDecoration: 'none' }}
                                                            >
                                                                https://edusphere.edu.sg/essential-resources-and-support-for-students/
                                                            </a>
                                                        </Text>
                                                    </Text>
                                                </Box>

                                                {/* Declaration by Applicant */}
                                                <Box
                                                    bg="white"
                                                    p={{base: 4, md: 6}}
                                                    borderRadius="lg"
                                                    boxShadow="sm"
                                                    borderWidth="1px"
                                                    borderColor="gray.200"
                                                    borderLeft="4px solid"
                                                    borderLeftColor="purple.500"
                                                >
                                                    <HStack mb={4} spacing={3}>
                                                        <Box
                                                            bg="purple.50"
                                                            p={2}
                                                            borderRadius="md"
                                                        >
                                                            <Text fontSize="xl">✍️</Text>
                                                        </Box>
                                                        <Heading 
                                                            size={{base: "sm", md: "md"}} 
                                                            color="gray.800"
                                                            fontWeight="bold"
                                                        >
                                                            Declaration by Applicant
                                                        </Heading>
                                                    </HStack>
                                                    <Box
                                                        bg="gray.50"
                                                        p={4}
                                                        borderRadius="md"
                                                        mb={4}
                                                        borderLeft="3px solid"
                                                        borderLeftColor="purple.400"
                                                    >
                                                        <Text 
                                                            fontSize={{base: "xs", md: "sm"}} 
                                                            color="gray.700" 
                                                            lineHeight="1.8"
                                                            fontStyle="italic"
                                                        >
                                                            I hereby declare that the information provided on this application is correct. I authorise {getTenantBranding().name || getTenantBranding().shortName} to obtain official records, if necessary, from any educational institution that I have attended. I understand that if any information is found to be false, this application may be cancelled at any stage.
                                                        </Text>
                                                    </Box>
                                                    
                                                    <Box
                                                        bg="blue.50"
                                                        p={4}
                                                        borderRadius="md"
                                                        borderWidth="2px"
                                                        borderColor={(isReadOnly && savedSignatureUrl) || hasAgreedToTerms ? "blue.400" : "gray.200"}
                                                    >
                                                        <Checkbox
                                                            isChecked={isReadOnly && savedSignatureUrl ? true : hasAgreedToTerms}
                                                            onChange={(e) => setHasAgreedToTerms(e.target.checked)}
                                                            isDisabled={isReadOnly}
                                                            colorScheme="blue"
                                                            size={{base: "sm", md: "md"}}
                                                        >
                                                            <Text 
                                                                fontSize={{base: "xs", md: "sm"}} 
                                                                color="gray.700"
                                                                fontWeight={(isReadOnly && savedSignatureUrl) || hasAgreedToTerms ? "medium" : "normal"}
                                                            >
                                                                I agree to the privacy policy and confirm that I have read the terms and conditions.
                                                            </Text>
                                                        </Checkbox>
                                                    </Box>
                                                </Box>

                                                {/* Signature Section — pad when editable; static image when read-only */}
                                                <Box
                                                    bg="white"
                                                    p={{base: 4, md: 6}}
                                                    borderRadius="lg"
                                                    boxShadow="sm"
                                                    borderWidth="1px"
                                                    borderColor={showSignatureCaptured ? "green.300" : "gray.200"}
                                                    borderLeft="4px solid"
                                                    borderLeftColor={showSignatureCaptured ? "green.500" : "orange.500"}
                                                    transition="all 0.3s ease"
                                                >
                                                    <FormControl isRequired={!isReadOnly}>
                                                        <HStack mb={3} spacing={2} justify="space-between" flexWrap="wrap">
                                                            <HStack spacing={2}>
                                                                <Box
                                                                    bg={showSignatureCaptured ? "green.50" : "orange.50"}
                                                                    p={1.5}
                                                                    borderRadius="md"
                                                                    transition="all 0.3s ease"
                                                                >
                                                                    <Text fontSize={{base: "lg", md: "xl"}}>
                                                                        {showSignatureCaptured ? "✓" : "🖊️"}
                                                                    </Text>
                                                                </Box>
                                                                <FormLabel 
                                                                    fontWeight="bold" 
                                                                    mb={0} 
                                                                    color={showSignatureCaptured ? "green.700" : "gray.800"}
                                                                    fontSize={{base: "sm", md: "md"}}
                                                                >
                                                                    Applicant Signature
                                                                </FormLabel>
                                                            </HStack>
                                                            {showSignatureCaptured && (
                                                                <Badge colorScheme="green" fontSize="xs">
                                                                    {isReadOnly ? "On file" : "Captured"}
                                                                </Badge>
                                                            )}
                                                        </HStack>

                                                        {isReadOnly ? (
                                                            <Box
                                                                border="2px solid"
                                                                borderColor={savedSignatureUrl ? "green.400" : "gray.300"}
                                                                borderRadius="md"
                                                                p={{base: 3, md: 4}}
                                                                bg="gray.50"
                                                                w="100%"
                                                                maxW="100%"
                                                            >
                                                                {savedSignatureUrl ? (
                                                                    <Center
                                                                        bg="white"
                                                                        borderRadius="md"
                                                                        borderWidth="1px"
                                                                        borderColor="gray.200"
                                                                        minH="160px"
                                                                        px={4}
                                                                        py={3}
                                                                    >
                                                                        <Box
                                                                            as="img"
                                                                            src={savedSignatureUrl}
                                                                            alt="Applicant signature on file"
                                                                            maxH="140px"
                                                                            maxW="100%"
                                                                            objectFit="contain"
                                                                            display="block"
                                                                        />
                                                                    </Center>
                                                                ) : (
                                                                    <Center minH="120px">
                                                                        <Text fontSize="sm" color="gray.500">
                                                                            No signature on file
                                                                        </Text>
                                                                    </Center>
                                                                )}
                                                                {formData.legal_declaration_signature_timestamp && (
                                                                    <Text fontSize="xs" color="gray.500" mt={2}>
                                                                        {formData.legal_declaration_signature_timestamp}
                                                                    </Text>
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            <>
                                                                <Box
                                                                    ref={signatureContainerRef}
                                                                    border="2px solid"
                                                                    borderColor={!isSignatureEmpty ? "green.400" : "gray.300"}
                                                                    borderRadius="md"
                                                                    p={{base: 2, md: 4}}
                                                                    bg="gray.50"
                                                                    w="100%"
                                                                    maxW="100%"
                                                                    position="relative"
                                                                    transition="border-color 0.2s"
                                                                    overflow="hidden"
                                                                >
                                                                    <Box
                                                                        w="100%"
                                                                        maxW="100%"
                                                                        position="relative"
                                                                        style={{
                                                                            touchAction: 'none',
                                                                            overflow: 'hidden'
                                                                        }}
                                                                    >
                                                                        <SignaturePad
                                                                            ref={signaturePadRef}
                                                                            clearOnResize={false}
                                                                            canvasProps={{
                                                                                className: "signatureCanvas",
                                                                                style: {
                                                                                    width: '100%',
                                                                                    maxWidth: '100%',
                                                                                    height: '200px',
                                                                                    display: 'block',
                                                                                    touchAction: 'none',
                                                                                    boxSizing: 'border-box'
                                                                                }
                                                                            }}
                                                                            onEnd={() => {
                                                                                const empty = signaturePadRef.current?.isEmpty() ?? true;
                                                                                if (!empty) {
                                                                                    signatureDataUrlRef.current = signaturePadRef.current.toDataURL();
                                                                                } else {
                                                                                    signatureDataUrlRef.current = null;
                                                                                }
                                                                                setIsSignatureEmpty(empty);
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                                <Flex 
                                                                    direction="row"
                                                                    gap={2} 
                                                                    mt={3}
                                                                    align="center"
                                                                    justify="flex-start"
                                                                >
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        colorScheme="gray"
                                                                        onClick={() => {
                                                                            if (signaturePadRef.current) {
                                                                                signaturePadRef.current.clear();
                                                                                signatureDataUrlRef.current = null;
                                                                                setIsSignatureEmpty(true);
                                                                            }
                                                                        }}
                                                                        disabled={isSignatureEmpty}
                                                                        fontSize="xs"
                                                                    >
                                                                        Clear
                                                                    </Button>
                                                                    <Text fontSize="xs" color="gray.500">
                                                                        Sign above using your finger or mouse
                                                                    </Text>
                                                                </Flex>
                                                            </>
                                                        )}
                                                    </FormControl>
                                                </Box>
                                            </VStack>
                                        </Box>

                                        {/* Payment Option Selection - ONLY AFTER SIGNATURE CAPTURED */}
                                        {!isSignatureEmpty && hasAgreedToTerms && !isReadOnly && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                            <Box 
                                                mt={{base: 6, md: 8}} 
                                                p={{base: 4, md: 6}} 
                                                borderWidth="2px" 
                                                borderRadius="xl" 
                                                borderColor="brand.400" 
                                                bg="brand.50" 
                                                boxShadow="lg"
                                            >
                                                <HStack spacing={2} mb={4}>
                                                    <Text fontSize={{base: "lg", md: "xl"}}>💳</Text>
                                                    <Heading fontWeight="bold" color="brand.700" size={{base: "sm", md: "md"}}>
                                                        Secure your seat
                                                    </Heading>
                                                </HStack>
                                                <Text fontSize="xs" color="gray.600" mb={4}>
                                                    Select your preferred payment method
                                                </Text>
                                                <RadioGroup 
                                                    onChange={(val) => setFormData(prev => ({ ...prev, payment_option: val }))} 
                                                    value={formData.payment_option || 'pay_now'}
                                                >
                                                    <Stack spacing={{base: 3, md: 4}}>
                                                        <Box 
                                                            p={4} 
                                                            borderWidth="1px" 
                                                            borderRadius="md" 
                                                            cursor="pointer"
                                                            borderColor={formData.payment_option === 'pay_now' ? "brand.500" : "gray.200"}
                                                            bg={formData.payment_option === 'pay_now' ? "brand.50" : "transparent"}
                                                            onClick={() => setFormData(prev => ({ ...prev, payment_option: 'pay_now' }))}
                                                        >
                                                            <Radio value="pay_now" colorScheme="brand">
                                                                <Text fontWeight="bold">PayNow Transfer</Text>
                                                                <Text fontSize="xs" color="gray.600">Pay via PayNow QR code or transfer</Text>
                                                            </Radio>
                                                        </Box>
                                                        {isBankTransferConfigured() && (
                                                        <Box 
                                                            p={4} 
                                                            borderWidth="1px" 
                                                            borderRadius="md" 
                                                            cursor="pointer"
                                                            borderColor={formData.payment_option === 'bank_transfer' ? "brand.500" : "gray.200"}
                                                            bg={formData.payment_option === 'bank_transfer' ? "brand.50" : "transparent"}
                                                            onClick={() => setFormData(prev => ({ ...prev, payment_option: 'bank_transfer' }))}
                                                        >
                                                            <Radio value="bank_transfer" colorScheme="brand">
                                                                <Text fontWeight="bold">Bank Transfer</Text>
                                                                <Text fontSize="xs" color="gray.600">Transfer directly to our bank account</Text>
                                                            </Radio>
                                                        </Box>
                                                        )}
                                                        {stripeEnabled && (
                                                        <Box 
                                                            p={4} 
                                                            borderWidth="1px" 
                                                            borderRadius="md" 
                                                            cursor="pointer"
                                                            borderColor={formData.payment_option === 'stripe' ? "brand.500" : "gray.200"}
                                                            bg={formData.payment_option === 'stripe' ? "brand.50" : "transparent"}
                                                            onClick={() => setFormData(prev => ({ ...prev, payment_option: 'stripe' }))}
                                                        >
                                                            <Radio value="stripe" colorScheme="brand">
                                                                <HStack spacing={2} align="center" flexWrap="wrap">
                                                                    <Text fontWeight="bold">Stripe</Text>
                                                                    <Box
                                                                        px={2}
                                                                        py={0.5}
                                                                        bg="red.50"
                                                                        borderRadius="sm"
                                                                        border="1px solid"
                                                                        borderColor="red.300"
                                                                    >
                                                                        <Text fontSize="2xs" color="red.600" fontWeight="semibold">Stripe Service Charge Applicable</Text>
                                                                    </Box>
                                                                </HStack>
                                                                <Text fontSize="xs" color="gray.600">Pay securely via credit card</Text>
                                                            </Radio>
                                                        </Box>
                                                        )}
                                                        <Box 
                                                            p={4} 
                                                            borderWidth="1px" 
                                                            borderRadius="md" 
                                                            cursor="pointer"
                                                            borderColor={formData.payment_option === 'pay_later_cash' ? "brand.500" : "gray.200"}
                                                            bg={formData.payment_option === 'pay_later_cash' ? "brand.50" : "transparent"}
                                                            onClick={() => setFormData(prev => ({ ...prev, payment_option: 'pay_later_cash' }))}
                                                        >
                                                            <Radio value="pay_later_cash" colorScheme="brand">
                                                                <Text fontWeight="bold">Pay later by Cash</Text>
                                                                <Text fontSize="xs" color="red.500" fontWeight="semibold">Seats are limited</Text>
                                                            </Radio>
                                                        </Box>
                                                    </Stack>
                                                </RadioGroup>
                                            </Box>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                )}

                                {/* Floating Action Button - Only shows when appropriate fields are filled */}
                                {!isReadOnly && ((!registrationId && progressData.canSave) || (registrationId && progressData.canSubmit)) && (
                                <Box
                                    position="fixed"
                                    bottom={0}
                                    left={0}
                                    right={0}
                                    bg="white"
                                    borderTop="1px solid"
                                    borderColor="gray.200"
                                    py={{base: 3, md: 4}}
                                    px={{base: 4, md: 6}}
                                    zIndex={50}
                                    boxShadow="0 -4px 20px rgba(0,0,0,0.1)"
                                >
                                <Flex 
                                    justifyContent="center"
                                    align="center"
                                    gap={3} 
                                    direction="column"
                                    maxW="600px"
                                    mx="auto"
                                >
                                    {!registrationId ? (
                                        <>
                                        <Button
                                            isLoading={isSaving}
                                            size="lg"
                                            width="100%"
                                            colorScheme="blue"
                                            onClick={handleSaveAndContinue}
                                            height={{base: "54px", md: "50px"}}
                                            fontSize={{base: "md", md: "md"}}
                                            boxShadow="lg"
                                            borderRadius="xl"
                                        >
                                            Save & Verify Email
                                        </Button>
                                        <Text fontSize="xs" color="gray.500" textAlign="center">
                                            All section fields complete — save to verify email
                                        </Text>
                                        </>
                                    ) : (
                                        <>
                                        <Button
                                            isLoading={isSubmitting}
                                            size="lg"
                                            width="100%"
                                            colorScheme="brand"
                                            height={{base: "54px", md: "50px"}}
                                            fontSize={{base: "md", md: "md"}}
                                            boxShadow="lg"
                                            borderRadius="xl"
                                            onClick={async () => {
                                                // 1. Validation for Email Verification (only if trying to submit)
                                                if (!emailVerified) {
                                                    toast({
                                                        title: "Email Verification Required",
                                                        description: "Please verify your email address before submitting. Check your inbox for the verification link.",
                                                        status: "error",
                                                        duration: 5000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                // 2. Validation for Agreement & Signature
                                                if (!hasAgreedToTerms) {
                                                    toast({
                                                        title: "Agreement Required",
                                                        description: "Please agree to the privacy policy and terms and conditions before submitting.",
                                                        status: "error",
                                                        duration: 5000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                if (isSignatureEmpty) {
                                                    toast({
                                                        title: "Signature Required",
                                                        description: "Please provide your signature before submitting.",
                                                        status: "error",
                                                        duration: 5000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                // 3. Validation for Required Fields
                                                const missingFields = [];
                                                if (!formData.nationality) missingFields.push("Nationality");
                                                if (!formData.nric) missingFields.push(isSingaporeCitizenOrPR(formData.nationality) ? "NRIC" : "Passport/FIN Number");
                                                if (!formData.dob) missingFields.push("Date of Birth");
                                                if (!formData.participant_name) missingFields.push("Participant Name");
                                                if (!formData.participant_email) missingFields.push("Email Address");
                                                if (!formData.mobile_no) missingFields.push("Mobile Number");

                                                // Check validation errors
                                                if (validationErrors.email) missingFields.push("Valid Email Address");
                                                if (validationErrors.mobile) missingFields.push("Valid Mobile Number");
                                                if (validationErrors.dob) missingFields.push("Valid Date of Birth");
                                                if (validationErrors.nric) missingFields.push("Valid Identification Number");

                                                if (missingFields.length > 0) {
                                                    toast({
                                                        title: "Missing Required Fields",
                                                        description: `Please fill in: ${missingFields.join(', ')}`,
                                                        status: "error",
                                                        duration: 5000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                // 4. Validation for Additional Required Fields (including staged files)
                                                if (!checkRequiredAdditionalFields()) {
                                                    // Get labels of missing fields
                                                    const missingAdditional = additionalFields.filter(field => {
                                                        const isRequired = getEffectiveRequiredStatus(field);
                                                        const fieldValue = getFieldValue(formData, field.field_key);
                                                        const stagedFiles = selectedFiles[field.field_key];
                                                        const hasValue = (fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0));
                                                        const hasStagedFiles = (stagedFiles && stagedFiles.length > 0);
                                                        return isRequired && !hasValue && !hasStagedFiles;
                                                    }).map(f => f.field_label);

                                                    toast({
                                                        title: "Missing Required Fields",
                                                        description: `Please fill in: ${missingAdditional.join(', ')}`,
                                                        status: "error",
                                                        duration: 5000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }

                                                // All checks passed, submit
                                                handleSubmit();
                                            }}
                                        >
                                            Submit Registration
                                        </Button>
                                        <Text fontSize="xs" color="gray.500" textAlign="center">
                                            All fields complete — ready to submit
                                        </Text>
                                        </>
                                    )}
                                </Flex>
                                </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Stack>
            )}

            {/* PayNow QR Modal */}
            <Modal isOpen={showPayNowModal} onClose={() => setShowPayNowModal(false)} size="lg" closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader textAlign="center">Secure Your Seat</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={6} py={4}>
                            {(() => {
                                // Calculate registration fee with GST (location-based exemption aware)
                                const rawFee = selectedCertType?.registration_fee;
                                const registrationFee = (rawFee === null || rawFee === undefined || rawFee === '')
                                    ? 0
                                    : (parseFloat(rawFee) || 0);
                                const applyGst = getEffectiveGstEnabled();
                                const gstRate = applyGst ? (orgGstSettings.gst_rate / 100) : 0;
                                const gstAmount = registrationFee * gstRate;
                                const totalWithGst = registrationFee + gstAmount;
                                
                                return (
                                    <VStack spacing={3}>
                                        <Text textAlign="center" fontSize="sm" color="gray.600">
                                            To secure your seat immediately, please pay the:
                                        </Text>
                                        <Text fontSize="md" fontWeight="bold" color="brand.600" textTransform="uppercase" letterSpacing="wider">Registration Fee</Text>
                                        
                                        <Box bg="gray.50" p={4} borderRadius="md" w="100%" minW="200px">
                                            <VStack spacing={2} align="stretch">
                                                <Flex justify="space-between">
                                                    <Text fontSize="sm" color="gray.600">Registration Fee:</Text>
                                                    <Text fontSize="sm" fontWeight="medium">S$ {registrationFee.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                                </Flex>
                                                {applyGst && gstAmount > 0 && (
                                                    <Flex justify="space-between">
                                                        <Text fontSize="sm" color="gray.600">GST ({orgGstSettings.gst_rate}%):</Text>
                                                        <Text fontSize="sm" fontWeight="medium">S$ {gstAmount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                                    </Flex>
                                                )}
                                                <Divider />
                                                <Flex justify="space-between" align="center">
                                                    <Text fontSize="md" fontWeight="bold" color="gray.700">Total:</Text>
                                                    <Heading size="lg" color="brand.700">S$ {totalWithGst.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Heading>
                                                </Flex>
                                            </VStack>
                                        </Box>
                                    </VStack>
                                );
                            })()}
                            
                            <VStack spacing={6} w="100%">
                                {/* Option 1: PayNow Transfer - Most Prominent */}
                                <Box 
                                    w="100%"
                                    p={6}
                                    borderWidth="2px"
                                    borderRadius="xl"
                                    bg="white"
                                    borderColor="green.300"
                                    boxShadow="md"
                                    _hover={{ borderColor: "green.400", boxShadow: "lg" }}
                                    transition="all 0.2s"
                                >
                                    <VStack spacing={4} align="stretch">
                                        <HStack spacing={3} align="center">
                                            <Icon as={HiQrcode} boxSize={6} color="green.600" />
                                            <Text fontWeight="bold" color="green.700" fontSize="lg">PayNow Transfer</Text>
                                            <Badge colorScheme="green" ml="auto">Fastest</Badge>
                                        </HStack>
                                        
                                        <Box bg="green.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="green.200">
                                            <VStack spacing={3} align="center">
                                                {(() => {
                                                    const rawFee = selectedCertType?.registration_fee;
                                                    const registrationFee = (rawFee === null || rawFee === undefined || rawFee === '')
                                                        ? 0
                                                        : (parseFloat(rawFee) || 0);
                                                    const applyGst = getEffectiveGstEnabled();
                                                    const gstRate = applyGst ? (orgGstSettings.gst_rate / 100) : 0;
                                                    const totalWithGst = registrationFee + registrationFee * gstRate;
                                                    // Fee 0/null → omit amount so payer can enter manually; keep reference
                                                    return (
                                                        <PayNowQRCode
                                                            uen={payNowOrg.uen}
                                                            amount={totalWithGst > 0 ? totalWithGst : null}
                                                            referenceNumber={payNowReference}
                                                            size={180}
                                                            showDownload
                                                            downloadLabel="Download QR Code"
                                                            downloadFileName={`PayNow-QR-${payNowReference || registrationId?.substring(0, 8)}.png`}
                                                        />
                                                    );
                                                })()}
                                            </VStack>
                                        </Box>

                                        <VStack spacing={2} align="stretch" fontSize="sm">
                                            <Box bg="gray.50" p={3} borderRadius="md">
                                                <Text fontWeight="semibold" color="gray.700" mb={1}>UEN No:</Text>
                                                <Text color="gray.800" fontFamily="mono" fontSize="md">{payNowOrg.uen || '—'}</Text>
                                            </Box>
                                            <Box bg="gray.50" p={3} borderRadius="md">
                                                <Text fontWeight="semibold" color="gray.700" mb={1}>Beneficiary Name:</Text>
                                                <Text color="gray.800">{payNowOrg.name || '—'}</Text>
                                            </Box>
                                            <Box bg="green.100" p={3} borderRadius="md" borderWidth="1px" borderColor="green.300">
                                                <Text fontWeight="bold" color="green.800" mb={1}>Reference Number:</Text>
                                                <Text color="green.900" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                    {payNowReference}
                                                </Text>
                                            </Box>
                                        </VStack>

                                        <Button 
                                            size="lg" 
                                            colorScheme="green" 
                                            w="100%"
                                            onClick={handlePayNowDone}
                                            isLoading={isUpdatingStatus}
                                            leftIcon={<Icon as={HiQrcode} />}
                                        >
                                            I have paid via PayNow
                                        </Button>
                                    </VStack>
                                </Box>

                                {/* Option 2: Bank Transfer — only when org bank details are set in TMS */}
                                {isBankTransferConfigured() && (
                                <Box 
                                    w="100%"
                                    p={6}
                                    borderWidth="2px"
                                    borderRadius="xl"
                                    bg="white"
                                    borderColor="purple.300"
                                    boxShadow="md"
                                    _hover={{ borderColor: "purple.400", boxShadow: "lg" }}
                                    transition="all 0.2s"
                                >
                                    <VStack spacing={4} align="stretch">
                                        <HStack spacing={3} align="center">
                                            <Icon as={HiOfficeBuilding} boxSize={6} color="purple.600" />
                                            <Text fontWeight="bold" color="purple.700" fontSize="lg">Bank Transfer</Text>
                                        </HStack>

                                        <Accordion allowToggle defaultIndex={0}>
                                            <AccordionItem border="none">
                                                <AccordionButton px={0} py={2} _hover={{ bg: "transparent" }}>
                                                    <Box flex="1" textAlign="left">
                                                        <Text fontWeight="semibold" color="gray.700">
                                                            View Bank Account Details
                                                        </Text>
                                                    </Box>
                                                    <AccordionIcon />
                                                </AccordionButton>
                                                <AccordionPanel px={0} pb={4}>
                                                    <VStack spacing={3} align="stretch" fontSize="sm">
                                                        <SimpleGrid columns={2} spacing={3}>
                                                            <Box bg="gray.50" p={3} borderRadius="md">
                                                                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Bank Name</Text>
                                                                <Text color="gray.800" fontWeight="medium">{getTenantBranding().bankName}</Text>
                                                            </Box>
                                                            {getTenantBranding().bankBranchCode && (
                                                            <Box bg="gray.50" p={3} borderRadius="md">
                                                                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Bank/Branch Code</Text>
                                                                <Text color="gray.800" fontFamily="mono" fontWeight="medium">{getTenantBranding().bankBranchCode}</Text>
                                                            </Box>
                                                            )}
                                                            {getTenantBranding().bankSwiftCode && (
                                                            <Box bg="gray.50" p={3} borderRadius="md">
                                                                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">SWIFT Code</Text>
                                                                <Text color="gray.800" fontFamily="mono" fontWeight="medium">{getTenantBranding().bankSwiftCode}</Text>
                                                            </Box>
                                                            )}
                                                            {getTenantBranding().bankAccountType && (
                                                            <Box bg="gray.50" p={3} borderRadius="md">
                                                                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Account Type</Text>
                                                                <Text color="gray.800" fontWeight="medium">{getTenantBranding().bankAccountType}</Text>
                                                            </Box>
                                                            )}
                                                        </SimpleGrid>
                                                        <Box bg="purple.50" p={3} borderRadius="md" borderWidth="1px" borderColor="purple.200">
                                                            <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Account Number</Text>
                                                            <Text color="purple.800" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                                {getTenantBranding().bankAccountNumber}
                                                            </Text>
                                                        </Box>
                                                        <Box bg="gray.50" p={3} borderRadius="md">
                                                            <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Beneficiary Name</Text>
                                                            <Text color="gray.800">{getTenantBranding().bankBeneficiaryName || getTenantBranding().name}</Text>
                                                        </Box>
                                                        <Box bg="purple.100" p={3} borderRadius="md" borderWidth="1px" borderColor="purple.300">
                                                            <Text fontWeight="bold" color="purple.800" mb={1} fontSize="xs">Reference Number</Text>
                                                            <Text color="purple.900" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                                {payNowReference}
                                                            </Text>
                                                        </Box>
                                                    </VStack>
                                                </AccordionPanel>
                                            </AccordionItem>
                                        </Accordion>

                                        <Button 
                                            size="lg" 
                                            colorScheme="purple" 
                                            w="100%"
                                            onClick={handlePayNowDone}
                                            isLoading={isUpdatingStatus}
                                            leftIcon={<Icon as={HiOfficeBuilding} />}
                                        >
                                            I have paid via Bank Transfer
                                        </Button>
                                    </VStack>
                                </Box>
                                )}

                                {/* Option 3: Stripe — only when tenant has Stripe keys configured */}
                                {stripeEnabled && (
                                <Box 
                                    w="100%"
                                    p={6}
                                    borderWidth="2px"
                                    borderRadius="xl"
                                    bg="white"
                                    borderColor="blue.300"
                                    boxShadow="md"
                                    _hover={{ borderColor: "blue.400", boxShadow: "lg" }}
                                    transition="all 0.2s"
                                >
                                    <VStack spacing={4} align="stretch">
                                        <HStack spacing={3} align="center">
                                            <Icon as={HiCreditCard} boxSize={6} color="blue.600" />
                                            <Text fontWeight="bold" color="blue.700" fontSize="lg">Stripe</Text>
                                            <Badge colorScheme="blue" ml="auto">Secure</Badge>
                                        </HStack>
                                        
                                        <Box bg="blue.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="blue.200">
                                            <VStack spacing={2} align="center">
                                                <Icon as={HiCreditCard} boxSize={10} color="blue.500" />
                                                <Text fontSize="sm" color="gray.700" textAlign="center">
                                                    Pay securely with your credit or debit card
                                                </Text>
                                            </VStack>
                                        </Box>

                                        <Button 
                                            size="lg" 
                                            colorScheme="blue" 
                                            w="100%"
                                            onClick={handleStripePayment}
                                            isLoading={isProcessingPayment}
                                            leftIcon={<Icon as={HiCreditCard} />}
                                        >
                                            Pay via Stripe
                                        </Button>
                                    </VStack>
                                </Box>
                                )}
                            </VStack>

                            <Alert status="info" borderRadius="md" size="sm">
                                <AlertIcon />
                                <Text fontSize="xs">
                                    Your registration is being processed. After payment, we will verify and send your LMS login details shortly.
                                </Text>
                            </Alert>
                        </VStack>
                    </ModalBody>
                    <ModalFooter bg="gray.50" borderBottomRadius="md">
                        <Button variant="ghost" mr={3} onClick={() => navigate(`/registration-pending/${registrationId}`)}>
                            I'll pay later
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
};

export default FullCertRegistration;

